import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Truck, ArrowRightLeft, Calculator, AlertTriangle, 
  CheckCircle, Clock, TrendingUp, TrendingDown, Plus, Search,
  Filter, Download, Upload, RefreshCw, Settings, BarChart3,
  Users, DollarSign, Target, Calendar, MapPin, Building2,
  ShoppingCart, Warehouse, ClipboardList, FileText, Eye,
  Edit, Trash2, MoreHorizontal, ChevronRight, ChevronLeft,
  Star, Heart, Zap, Activity, Globe, Shield, Award, X,
  Camera, QrCode, Barcode, Scan, Printer, Bell, UserCheck,
  Store, Home, Database, PieChart, LineChart, BarChart2,
  Palette, Sparkles, Layers, Grid, List, ChevronDown, 
  Menu, Settings2, User, Mail, Phone, MapPin2, CreditCard,
  Lock, Unlock, EyeOff, Volume2, VolumeX, Wifi, WifiOff,
  Battery, BatteryCharging, Signal, SignalHigh, SignalLow,
  Moon, Sun, Cloud, CloudRain, CloudSnow, Wind, Thermometer,
  Droplets, Flame, Leaf, TreePine, Mountain, Waves, Fish,
  Car, Plane, Train, Ship, Bike, Walk, Run, Coffee, Wine,
  Pizza, Hamburger, IceCream, Cake, Cookie, Apple, Carrot,
  Beef, Chicken, Fish2, Egg, Milk, Cheese, Bread, Rice,
  Salt, Pepper, Oil, Honey, Jam, Ketchup, Mustard, Vinegar
} from 'lucide-react';
import { 
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart as RechartsBarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart as RechartsPieChart, 
  Pie, Cell, ComposedChart
} from 'recharts';

// Import inventory components
import ReceivingManagement from './components/ReceivingManagement';
import TransferManagement from './components/TransferManagement';
import InventoryCounting from './components/InventoryCounting';
import OrderingManagement from './components/OrderingManagement';
import InventoryReports from './components/InventoryReports';
import StoreInventory from './components/StoreInventory';

const Inventory = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStore, setSelectedStore] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Mock data for comprehensive inventory
  const inventoryData = [
    {
      id: 1,
      sku: 'INV-001',
      name: 'Premium Coffee Beans',
      category: 'Beverages',
      status: 'in-stock',
      quantity: 150,
      minStock: 20,
      maxStock: 200,
      unit: 'kg',
      cost: 25.50,
      price: 45.00,
      location: 'Warehouse A',
      supplier: 'Coffee Co.',
      lastUpdated: '2024-01-15',
      expiryDate: '2024-06-15',
      inTransit: 50,
      ordered: 100,
      received: 0,
      barcode: '1234567890123',
      qrCode: 'INV-001-QR',
      images: ['coffee-beans-1.jpg', 'coffee-beans-2.jpg'],
      notes: 'Premium Arabica beans from Colombia'
    },
    {
      id: 2,
      sku: 'INV-002',
      name: 'Organic Milk',
      category: 'Dairy',
      status: 'low-stock',
      quantity: 15,
      minStock: 30,
      maxStock: 150,
      unit: 'liters',
      cost: 3.20,
      price: 6.50,
      location: 'Cold Storage',
      supplier: 'Dairy Farm',
      lastUpdated: '2024-01-14',
      expiryDate: '2024-01-25',
      inTransit: 80,
      ordered: 120,
      received: 0,
      barcode: '1234567890124',
      qrCode: 'INV-002-QR',
      images: ['milk-1.jpg'],
      notes: 'Temperature controlled storage required'
    },
    {
      id: 3,
      sku: 'INV-003',
      name: 'Artisan Bread',
      category: 'Bakery',
      status: 'out-of-stock',
      quantity: 0,
      minStock: 50,
      maxStock: 200,
      unit: 'pieces',
      cost: 2.50,
      price: 5.00,
      location: 'Bakery Section',
      supplier: 'Local Bakery',
      lastUpdated: '2024-01-13',
      expiryDate: '2024-01-16',
      inTransit: 100,
      ordered: 150,
      received: 0,
      barcode: '1234567890125',
      qrCode: 'INV-003-QR',
      images: ['bread-1.jpg', 'bread-2.jpg'],
      notes: 'Fresh daily delivery'
    },
    {
      id: 4,
      sku: 'INV-004',
      name: 'Fresh Vegetables Mix',
      category: 'Produce',
      status: 'in-stock',
      quantity: 75,
      minStock: 25,
      maxStock: 100,
      unit: 'kg',
      cost: 4.80,
      price: 8.90,
      location: 'Produce Section',
      supplier: 'Fresh Farms',
      lastUpdated: '2024-01-15',
      expiryDate: '2024-01-22',
      inTransit: 30,
      ordered: 60,
      received: 0,
      barcode: '1234567890126',
      qrCode: 'INV-004-QR',
      images: ['vegetables-1.jpg'],
      notes: 'Organic certified'
    },
    {
      id: 5,
      sku: 'INV-005',
      name: 'Premium Tea Selection',
      category: 'Beverages',
      status: 'in-stock',
      quantity: 200,
      minStock: 40,
      maxStock: 300,
      unit: 'boxes',
      cost: 15.00,
      price: 28.00,
      location: 'Warehouse B',
      supplier: 'Tea Masters',
      lastUpdated: '2024-01-15',
      expiryDate: '2025-01-15',
      inTransit: 0,
      ordered: 50,
      received: 0,
      barcode: '1234567890127',
      qrCode: 'INV-005-QR',
      images: ['tea-1.jpg', 'tea-2.jpg', 'tea-3.jpg'],
      notes: 'Assorted premium teas'
    }
  ];

  const chartData = [
    { month: 'Jan', stock: 1200, received: 800, transferred: 400, count: 1150, ordered: 900 },
    { month: 'Feb', stock: 1350, received: 900, transferred: 450, count: 1300, ordered: 950 },
    { month: 'Mar', stock: 1100, received: 700, transferred: 600, count: 1050, ordered: 800 },
    { month: 'Apr', stock: 1400, received: 1000, transferred: 500, count: 1350, ordered: 1100 },
    { month: 'May', stock: 1250, received: 850, transferred: 550, count: 1200, ordered: 900 },
    { month: 'Jun', stock: 1500, received: 1100, transferred: 400, count: 1450, ordered: 1200 }
  ];

  const categoryData = [
    { name: 'Beverages', value: 35, color: '#8B5CF6' },
    { name: 'Dairy', value: 25, color: '#3B82F6' },
    { name: 'Bakery', value: 20, color: '#F59E0B' },
    { name: 'Produce', value: 15, color: '#10B981' },
    { name: 'Others', value: 5, color: '#EF4444' }
  ];

  const statusData = [
    { name: 'In Stock', value: 60, color: '#10B981' },
    { name: 'Low Stock', value: 25, color: '#F59E0B' },
    { name: 'Out of Stock', value: 10, color: '#EF4444' },
    { name: 'In Transit', value: 5, color: '#3B82F6' }
  ];

  const stores = [
    { id: 1, name: 'Store 1', location: 'Riyadh', status: 'active' },
    { id: 2, name: 'Store 2', location: 'Jeddah', status: 'active' },
    { id: 3, name: 'Store 3', location: 'Dammam', status: 'active' },
    { id: 4, name: 'Store 4', location: 'Abha', status: 'maintenance' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'in-stock': return 'bg-green-100 text-green-800';
      case 'low-stock': return 'bg-yellow-100 text-yellow-800';
      case 'out-of-stock': return 'bg-red-100 text-red-800';
      case 'in-transit': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in-stock': return <CheckCircle className="w-4 h-4" />;
      case 'low-stock': return <AlertTriangle className="w-4 h-4" />;
      case 'out-of-stock': return <X className="w-4 h-4" />;
      case 'in-transit': return <Truck className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const filteredData = inventoryData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.barcode.includes(searchQuery);
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const totalValue = inventoryData.reduce((sum, item) => sum + (item.quantity * item.cost), 0);
  const totalItems = inventoryData.reduce((sum, item) => sum + item.quantity, 0);
  const lowStockItems = inventoryData.filter(item => item.status === 'low-stock').length;
  const outOfStockItems = inventoryData.filter(item => item.status === 'out-of-stock').length;

  const MetricCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">
            {title}
          </p>
          <p className="text-2xl font-bold mt-1 text-gray-900">
            {value}
          </p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${
              change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const InventoryCard = ({ item }) => (
    <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            {item.name}
          </h3>
          <p className="text-sm text-gray-600">
            SKU: {item.sku} • {item.category}
          </p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 text-white ${
          item.status === 'in-stock' ? 'bg-green-600' :
          item.status === 'low-stock' ? 'bg-yellow-600' :
          'bg-red-600'
        }`}>
          {getStatusIcon(item.status)}
          <span>{item.status.replace('-', ' ').toUpperCase()}</span>
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Current Stock</p>
            <p className="font-medium text-gray-900">
              {item.quantity} {item.unit}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Location</p>
            <p className="font-medium text-gray-900">
              {item.location}
            </p>
          </div>
        </div>

        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Stock Level</span>
            <span className="text-gray-900">
              {Math.round((item.quantity / item.maxStock) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                item.quantity < item.minStock ? 'bg-red-500' :
                item.quantity < item.maxStock * 0.3 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center p-2 bg-blue-600 rounded-lg text-white">
            <p className="font-medium">{item.inTransit}</p>
            <p className="opacity-90">In Transit</p>
          </div>
          <div className="text-center p-2 bg-yellow-600 rounded-lg text-white">
            <p className="font-medium">{item.ordered}</p>
            <p className="opacity-90">Ordered</p>
          </div>
          <div className="text-center p-2 bg-green-600 rounded-lg text-white">
            <p className="font-medium">{item.received}</p>
            <p className="opacity-90">Received</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-600">
              Expires: {new Date(item.expiryDate).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <QrCode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const InventoryListView = ({ item }) => (
    <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{item.name}</h3>
            <p className="text-sm text-gray-600">SKU: {item.sku} • {item.category}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{item.quantity} {item.unit}</p>
            <p className="text-xs text-gray-600">Current Stock</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
              item.status === 'in-stock' ? 'bg-green-600' :
              item.status === 'low-stock' ? 'bg-yellow-600' :
              'bg-red-600'
            }`}>
              {item.status.replace('-', ' ').toUpperCase()}
            </span>
            {item.inTransit > 0 && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                {item.inTransit} In Transit
              </span>
            )}
            {item.ordered > 0 && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-600 text-white">
                {item.ordered} Ordered
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <QrCode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children }) => (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">
        {title}
      </h3>
      {children}
    </div>
  );

  const QuickActionCard = ({ icon: Icon, title, description, color, onClick }) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all text-left"
    >
      <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </motion.button>
  );

     const navigationTabs = [
     { id: 'overview', label: 'Overview', icon: BarChart3 },
     { id: 'receiving', label: 'Receiving', icon: Truck },
     { id: 'transferring', label: 'Transferring', icon: ArrowRightLeft },
     { id: 'counting', label: 'Counting', icon: Calculator },
     { id: 'ordering', label: 'Ordering', icon: ShoppingCart },
     { id: 'stores', label: 'Store Management', icon: Store },
     { id: 'reports', label: 'Reports', icon: FileText },
     { id: 'settings', label: 'Settings', icon: Settings },
     { id: 'elements', label: 'Elements', icon: Palette }
   ];

  const quickActions = [
    {
      icon: Plus,
      title: 'Add New Item',
      description: 'Create a new inventory item with barcode and QR code',
      color: 'bg-blue-500',
      onClick: () => console.log('Add new item')
    },
    {
      icon: Camera,
      title: 'Scan Barcode',
      description: 'Use camera to scan barcodes and QR codes',
      color: 'bg-green-500',
      onClick: () => console.log('Scan barcode')
    },
    {
      icon: Calculator,
      title: 'Start Counting',
      description: 'Begin physical inventory count with camera support',
      color: 'bg-purple-500',
      onClick: () => console.log('Start counting')
    },
    {
      icon: Truck,
      title: 'Receive Items',
      description: 'Process incoming shipments and verify items',
      color: 'bg-orange-500',
      onClick: () => console.log('Receive items')
    },
    {
      icon: ArrowRightLeft,
      title: 'Transfer Items',
      description: 'Move inventory between locations',
      color: 'bg-indigo-500',
      onClick: () => console.log('Transfer items')
    },
    {
      icon: Printer,
      title: 'Print Labels',
      description: 'Generate and print barcode labels',
      color: 'bg-red-500',
      onClick: () => console.log('Print labels')
    }
  ];

  return (
    <div className="space-y-6">
             {/* Header */}
       <div className="bg-indigo-700 rounded-xl p-6 text-white">
         {/* Content */}
         <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Inventory Management</h1>
            <p className="text-indigo-100 mt-1">Comprehensive inventory tracking and management system</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-white text-indigo-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 font-medium"
          >
            <Plus className="w-5 h-5" />
            Add Item
          </motion.button>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
            {navigationTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                                     className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                     activeTab === tab.id
                       ? 'bg-white text-indigo-700'
                       : 'text-white/90 hover:bg-white/10'
                   }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Total Value"
                value={`$${totalValue.toLocaleString()}`}
                change={12}
                icon={DollarSign}
                color="bg-green-500"
              />
              <MetricCard
                title="Total Items"
                value={totalItems.toLocaleString()}
                change={8}
                icon={Package}
                color="bg-blue-500"
              />
              <MetricCard
                title="Low Stock Items"
                value={lowStockItems}
                change={-5}
                icon={AlertTriangle}
                color="bg-yellow-500"
              />
              <MetricCard
                title="Out of Stock"
                value={outOfStockItems}
                change={-15}
                icon={X}
                color="bg-red-500"
              />
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickActions.map((action, index) => (
                  <QuickActionCard key={index} {...action} />
                ))}
              </div>
            </div>

            {/* Enhanced Search and Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Search & Filter</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by name, SKU, or barcode..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                </div>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                >
                  <option value="all">All Status</option>
                  <option value="in-stock">In Stock</option>
                  <option value="low-stock">Low Stock</option>
                  <option value="out-of-stock">Out of Stock</option>
                  <option value="in-transit">In Transit</option>
                </select>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                >
                  <option value="all">All Categories</option>
                  <option value="Beverages">Beverages</option>
                  <option value="Dairy">Dairy</option>
                  <option value="Bakery">Bakery</option>
                  <option value="Produce">Produce</option>
                </select>
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                >
                  <option value="all">All Stores</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Charts */}
              <div className="lg:col-span-2 space-y-6">
                {/* Performance Overview Chart */}
                <ChartCard title="Inventory Trends">
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" />
                      <YAxis stroke="#6B7280" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="stock" 
                        stackId="1" 
                        stroke="#8B5CF6" 
                        fill="#8B5CF6" 
                        fillOpacity={0.3} 
                      />
                      <Bar dataKey="received" fill="#3B82F6" />
                      <Line type="monotone" dataKey="transferred" stroke="#F59E0B" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Category Distribution */}
                <ChartCard title="Category Distribution">
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Current Inventory Status */}
                <ChartCard title="Current Inventory Status">
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={[
                          { name: 'In Stock', value: inventoryData.filter(item => item.status === 'in-stock').length, fill: '#10B981' },
                          { name: 'Low Stock', value: inventoryData.filter(item => item.status === 'low-stock').length, fill: '#F59E0B' },
                          { name: 'Out of Stock', value: inventoryData.filter(item => item.status === 'out-of-stock').length, fill: '#EF4444' },
                          { name: 'In Transit', value: inventoryData.filter(item => item.inTransit > 0).length, fill: '#8B5CF6' }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Variance Analysis */}
                <ChartCard title="Counting Variance Analysis">
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={inventoryData.filter(item => item.quantity !== item.expectedQuantity).slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" stroke="#6B7280" angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#6B7280" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="expectedQuantity" fill="#3B82F6" name="Expected" />
                      <Bar dataKey="quantity" fill="#EF4444" name="Actual" />
                      <Line type="monotone" dataKey="variance" stroke="#F59E0B" strokeWidth={2} name="Variance" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* Right Column - Inventory List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Inventory Items
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'grid' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'list' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-4' : 'space-y-4'}>
                  {filteredData.map((item) => (
                    viewMode === 'grid' ? (
                      <InventoryCard 
                        key={item.id} 
                        item={item} 
                      />
                    ) : (
                      <InventoryListView 
                        key={item.id} 
                        item={item} 
                      />
                    )
                  ))}
                </div>
                              </div>
              </div>

              {/* Form Elements Section */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">3. Form Elements</h2>
                
                {/* Search Boxes */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search 1 - Basic"
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search 2 - Rounded"
                      className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                  
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search 3 - Glass"
                      className="w-full px-4 py-3 rounded-lg bg-gray-800/80 backdrop-blur-sm border border-gray-600 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                  </div>
                </div>

                {/* Dropdown Menus */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="relative">
                    <select className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white">
                      <option>Dropdown 1</option>
                      <option>Option 1</option>
                      <option>Option 2</option>
                      <option>Option 3</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                  
                  <div className="relative">
                    <select className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 appearance-none bg-white">
                      <option>Dropdown 2</option>
                      <option>Option 1</option>
                      <option>Option 2</option>
                      <option>Option 3</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                  
                  <div className="relative">
                    <select className="w-full px-4 py-3 rounded-lg bg-gray-50 border-0 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:bg-white">
                      <option>Dropdown 3</option>
                      <option>Option 1</option>
                      <option>Option 2</option>
                      <option>Option 3</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                  
                  <div className="relative">
                    <select className="w-full px-4 py-3 rounded-lg border-2 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none bg-white">
                      <option>Dropdown 4</option>
                      <option>Option 1</option>
                      <option>Option 2</option>
                      <option>Option 3</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
                  </div>
                </div>

                {/* Input Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Input 1 - Standard</label>
                    <input
                      type="text"
                      placeholder="Enter text..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Input 2 - With Icon</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Username"
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Input 3 - Floating</label>
                    <div className="relative">
                      <input
                        type="email"
                        placeholder=" "
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 peer"
                      />
                      <label className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 transition-all peer-focus:-top-2 peer-focus:left-2 peer-focus:text-xs peer-focus:text-purple-500 peer-placeholder-shown:top-1/2 peer-placeholder-shown:left-3 peer-placeholder-shown:text-base">
                        Email Address
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">4. Chart Examples</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Line Chart */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Chart 1 - Line Chart</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <RechartsLineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="month" stroke="#6B7280" />
                        <YAxis stroke="#6B7280" />
                        <Tooltip />
                        <Line type="monotone" dataKey="stock" stroke="#3B82F6" strokeWidth={2} />
                        <Line type="monotone" dataKey="received" stroke="#10B981" strokeWidth={2} />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Bar Chart */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Chart 2 - Bar Chart</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <RechartsBarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="month" stroke="#6B7280" />
                        <YAxis stroke="#6B7280" />
                        <Tooltip />
                        <Bar dataKey="stock" fill="#3B82F6" />
                        <Bar dataKey="received" fill="#10B981" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Pie Chart */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Chart 3 - Pie Chart</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <RechartsPieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Area Chart */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Chart 4 - Area Chart</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="month" stroke="#6B7280" />
                        <YAxis stroke="#6B7280" />
                        <Tooltip />
                        <Area type="monotone" dataKey="stock" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                        <Area type="monotone" dataKey="received" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Stock Level Visualizations */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">5. Stock Level Visualizations</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Progress Bar */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Stock Level 1 - Progress Bar</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Coffee Beans</span>
                          <span className="text-gray-900">75%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Milk</span>
                          <span className="text-gray-900">10%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-red-500 h-2 rounded-full" style={{ width: '10%' }}></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Bread</span>
                          <span className="text-gray-900">45%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '45%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Circular Progress */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Stock Level 2 - Circular</h3>
                    <div className="flex justify-center">
                      <div className="relative w-24 h-24">
                        <svg className="w-24 h-24 transform -rotate-90">
                          <circle
                            cx="48"
                            cy="48"
                            r="36"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="transparent"
                            className="text-gray-200"
                          />
                          <circle
                            cx="48"
                            cy="48"
                            r="36"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="transparent"
                            strokeDasharray={`${2 * Math.PI * 36}`}
                            strokeDashoffset={`${2 * Math.PI * 36 * (1 - 0.75)}`}
                            className="text-green-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-semibold text-gray-900">75%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gauge Chart */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Stock Level 3 - Gauge</h3>
                    <div className="flex justify-center">
                      <div className="relative w-24 h-24">
                        <svg className="w-24 h-24 transform -rotate-90">
                          <path
                            d="M 48 12 A 36 36 0 0 1 84 48"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="transparent"
                            className="text-gray-200"
                          />
                          <path
                            d="M 48 12 A 36 36 0 0 1 84 48"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="transparent"
                            strokeDasharray={`${Math.PI * 36}`}
                            strokeDashoffset={`${Math.PI * 36 * (1 - 0.6)}`}
                            className="text-blue-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-semibold text-gray-900">60%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stacked Bar */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Stock Level 4 - Stacked</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-8 bg-green-500 rounded"></div>
                        <div className="w-8 h-8 bg-yellow-500 rounded"></div>
                        <div className="w-4 h-8 bg-red-500 rounded"></div>
                        <span className="text-sm text-gray-600">Item A</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-12 h-8 bg-green-500 rounded"></div>
                        <div className="w-12 h-8 bg-yellow-500 rounded"></div>
                        <div className="w-4 h-8 bg-red-500 rounded"></div>
                        <span className="text-sm text-gray-600">Item B</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-8 bg-green-500 rounded"></div>
                        <div className="w-8 h-8 bg-yellow-500 rounded"></div>
                        <div className="w-16 h-8 bg-red-500 rounded"></div>
                        <span className="text-sm text-gray-600">Item C</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Indicators */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">6. Status Indicators</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Status Badges */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Status 1 - Badges</h3>
                    <div className="space-y-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        In Stock
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                        Low Stock
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                        Out of Stock
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        In Transit
                      </span>
                    </div>
                  </div>

                  {/* Status Cards */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Status 2 - Cards</h3>
                    <div className="space-y-3">
                      <div className="flex items-center p-3 bg-green-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-green-900">Available</p>
                          <p className="text-xs text-green-600">150 units in stock</p>
                        </div>
                      </div>
                      <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-yellow-900">Low Stock</p>
                          <p className="text-xs text-yellow-600">15 units remaining</p>
                        </div>
                      </div>
                      <div className="flex items-center p-3 bg-red-50 rounded-lg">
                        <X className="w-5 h-5 text-red-600 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-red-900">Out of Stock</p>
                          <p className="text-xs text-red-600">0 units available</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Icons */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Status 3 - Icons</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <p className="text-xs text-gray-600">Active</p>
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                        <p className="text-xs text-gray-600">Pending</p>
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <X className="w-6 h-6 text-red-600" />
                        </div>
                        <p className="text-xs text-gray-600">Inactive</p>
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Truck className="w-6 h-6 text-blue-600" />
                        </div>
                        <p className="text-xs text-gray-600">Shipping</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Timeline */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold mb-4">Status 4 - Timeline</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Order Placed</p>
                          <p className="text-xs text-gray-500">2 hours ago</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Processing</p>
                          <p className="text-xs text-gray-500">1 hour ago</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-gray-300 rounded-full mr-3"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-400">Shipped</p>
                          <p className="text-xs text-gray-400">Pending</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Receiving Tab Content */}
        {activeTab === 'receiving' && (
          <ReceivingManagement />
        )}

        {/* Transferring Tab Content */}
        {activeTab === 'transferring' && (
          <TransferManagement />
        )}

        {/* Counting Tab Content */}
        {activeTab === 'counting' && (
          <InventoryCounting />
        )}

        {/* Ordering Tab Content */}
        {activeTab === 'ordering' && (
          <OrderingManagement />
        )}

        {/* Store Management Tab Content */}
        {activeTab === 'stores' && (
          <StoreInventory />
        )}

        {/* Reports Tab Content */}
        {activeTab === 'reports' && (
          <InventoryReports />
        )}

                 {/* Settings Tab Content */}
         {activeTab === 'settings' && (
           <div className="bg-white rounded-xl border border-gray-200 p-6">
             <h2 className="text-2xl font-bold mb-4 text-gray-900">
               Inventory Settings
             </h2>
             <p className="text-lg text-gray-600 mb-6">
               Configure inventory parameters, alerts, and system preferences.
             </p>
             {/* Settings content will be implemented here */}
           </div>
         )}

         {/* Elements Tab Content */}
         {activeTab === 'elements' && (
           <div className="space-y-8">
             {/* Header */}
             <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 rounded-xl p-8 text-white">
               <h1 className="text-3xl font-bold mb-2">UI Elements Library</h1>
               <p className="text-purple-100">Comprehensive collection of UI components, styles, and patterns</p>
             </div>

             {/* Card Styles Section */}
             <div className="space-y-6">
               <h2 className="text-2xl font-bold text-gray-900">1. Card Styles</h2>
               
               {/* Glass Effect Cards */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 <div className="backdrop-blur-md bg-gray-900/80 border border-gray-700 rounded-xl p-6 shadow-xl">
                   <h3 className="text-lg font-semibold text-white mb-2">Glass Card 1</h3>
                   <p className="text-gray-200">Frosted glass effect with blur backdrop</p>
                 </div>
                 
                 <div className="backdrop-blur-lg bg-black/20 border border-white/20 rounded-2xl p-6 shadow-2xl">
                   <h3 className="text-lg font-semibold text-white mb-2">Glass Card 2</h3>
                   <p className="text-gray-200">Dark glass with heavy blur effect</p>
                 </div>
                 
                 <div className="backdrop-blur-sm bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-white/40 rounded-xl p-6">
                   <h3 className="text-lg font-semibold text-white mb-2">Glass Card 3</h3>
                   <p className="text-gray-100">Gradient glass with subtle blur</p>
                 </div>
               </div>

               {/* Minimal Cards */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                 <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                   <h3 className="font-medium text-gray-900 mb-1">Minimal 1</h3>
                   <p className="text-sm text-gray-600">Clean and simple design</p>
                 </div>
                 
                 <div className="bg-gray-50 border-0 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                   <h3 className="font-medium text-gray-900 mb-1">Minimal 2</h3>
                   <p className="text-sm text-gray-600">No border, subtle background</p>
                 </div>
                 
                 <div className="bg-white shadow-sm border border-gray-100 rounded-xl p-4">
                   <h3 className="font-medium text-gray-900 mb-1">Minimal 3</h3>
                   <p className="text-sm text-gray-600">Soft shadow and rounded corners</p>
                 </div>
                 
                 <div className="bg-white border-l-4 border-blue-500 rounded-r-lg p-4">
                   <h3 className="font-medium text-gray-900 mb-1">Minimal 4</h3>
                   <p className="text-sm text-gray-600">Left border accent</p>
                 </div>
               </div>

               {/* Animated Cards */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 <motion.div 
                   whileHover={{ scale: 1.05, rotate: 1 }}
                   whileTap={{ scale: 0.95 }}
                   className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white shadow-lg"
                 >
                   <h3 className="text-lg font-semibold mb-2">Animated 1</h3>
                   <p>Scale and rotate on hover</p>
                 </motion.div>
                 
                 <motion.div 
                   whileHover={{ y: -10 }}
                   className="bg-gradient-to-br from-green-400 to-blue-500 rounded-xl p-6 text-white shadow-lg"
                 >
                   <h3 className="text-lg font-semibold mb-2">Animated 2</h3>
                   <p>Lift up on hover</p>
                 </motion.div>
                 
                 <motion.div 
                   whileHover={{ scale: 1.02 }}
                   className="bg-gradient-to-br from-pink-500 to-red-500 rounded-xl p-6 text-white shadow-lg"
                 >
                   <h3 className="text-lg font-semibold mb-2">Animated 3</h3>
                   <p>Subtle scale effect</p>
                 </motion.div>
               </div>
             </div>

             {/* Button Styles Section */}
             <div className="space-y-6">
               <h2 className="text-2xl font-bold text-gray-900">2. Button Styles</h2>
               
               <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                 <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                   Primary
                 </button>
                 <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors">
                   Secondary
                 </button>
                 <button className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors">
                   Outline
                 </button>
                 <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors">
                   Danger
                 </button>
                 <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                   Success
                 </button>
                 <button className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors">
                   Warning
                 </button>
               </div>

               {/* Floating Action Buttons */}
               <div className="relative h-32 bg-gray-100 rounded-lg p-4">
                 <motion.button 
                   whileHover={{ scale: 1.1 }}
                   whileTap={{ scale: 0.9 }}
                   className="absolute bottom-4 right-4 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
                 >
                   <Plus className="w-6 h-6" />
                 </motion.button>
                 
                 <motion.button 
                   whileHover={{ scale: 1.1 }}
                   whileTap={{ scale: 0.9 }}
                   className="absolute bottom-4 right-20 w-12 h-12 bg-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
                 >
                   <Camera className="w-5 h-5" />
                 </motion.button>
                 
                 <motion.button 
                   whileHover={{ scale: 1.1 }}
                   whileTap={{ scale: 0.9 }}
                   className="absolute bottom-4 right-32 w-12 h-12 bg-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
                 >
                   <QrCode className="w-5 h-5" />
                 </motion.button>
               </div>

               {/* Form Elements Section */}
               <div className="space-y-6">
                 <h2 className="text-2xl font-bold text-gray-900">3. Form Elements</h2>
                 
                 {/* Search Boxes */}
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   <div className="relative">
                     <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                     <input
                       type="text"
                       placeholder="Search 1 - Basic"
                       className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                     />
                   </div>
                   
                   <div className="relative">
                     <input
                       type="text"
                       placeholder="Search 2 - Rounded"
                       className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                     />
                     <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                   </div>
                   
                   <div className="relative">
                     <input
                       type="text"
                       placeholder="Search 3 - Glass"
                       className="w-full px-4 py-3 rounded-lg bg-gray-800/80 backdrop-blur-sm border border-gray-600 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                     />
                     <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                   </div>
                 </div>

                 {/* Dropdown Menus */}
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                   <div className="relative">
                     <select className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white">
                       <option>Dropdown 1</option>
                       <option>Option 1</option>
                       <option>Option 2</option>
                       <option>Option 3</option>
                     </select>
                     <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                   </div>
                   
                   <div className="relative">
                     <select className="w-full px-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 appearance-none bg-white">
                       <option>Dropdown 2</option>
                       <option>Option 1</option>
                       <option>Option 2</option>
                       <option>Option 3</option>
                     </select>
                     <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                   </div>
                   
                   <div className="relative">
                     <select className="w-full px-4 py-3 rounded-lg bg-gray-50 border-0 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:bg-white">
                       <option>Dropdown 3</option>
                       <option>Option 1</option>
                       <option>Option 2</option>
                       <option>Option 3</option>
                     </select>
                     <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                   </div>
                   
                   <div className="relative">
                     <select className="w-full px-4 py-3 rounded-lg border-2 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none bg-white">
                       <option>Dropdown 4</option>
                       <option>Option 1</option>
                       <option>Option 2</option>
                       <option>Option 3</option>
                     </select>
                     <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
                   </div>
                 </div>

                 {/* Input Fields */}
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Input 1 - Standard</label>
                     <input
                       type="text"
                       placeholder="Enter text..."
                       className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                     />
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Input 2 - With Icon</label>
                     <div className="relative">
                       <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                       <input
                         type="text"
                         placeholder="Username"
                         className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                       />
                     </div>
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Input 3 - Floating</label>
                     <div className="relative">
                       <input
                         type="email"
                         placeholder=" "
                         className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 peer"
                       />
                       <label className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 transition-all peer-focus:-top-2 peer-focus:left-2 peer-focus:text-xs peer-focus:text-purple-500 peer-placeholder-shown:top-1/2 peer-placeholder-shown:left-3 peer-placeholder-shown:text-base">
                         Email Address
                       </label>
                     </div>
                   </div>
                 </div>
               </div>

               {/* Charts Section */}
               <div className="space-y-6">
                 <h2 className="text-2xl font-bold text-gray-900">4. Chart Examples</h2>
                 
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                   {/* Line Chart */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Chart 1 - Line Chart</h3>
                     <ResponsiveContainer width="100%" height={200}>
                       <RechartsLineChart data={chartData}>
                         <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                         <XAxis dataKey="month" stroke="#6B7280" />
                         <YAxis stroke="#6B7280" />
                         <Tooltip />
                         <Line type="monotone" dataKey="stock" stroke="#3B82F6" strokeWidth={2} />
                         <Line type="monotone" dataKey="received" stroke="#10B981" strokeWidth={2} />
                       </RechartsLineChart>
                     </ResponsiveContainer>
                   </div>

                   {/* Bar Chart */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Chart 2 - Bar Chart</h3>
                     <ResponsiveContainer width="100%" height={200}>
                       <RechartsBarChart data={chartData}>
                         <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                         <XAxis dataKey="month" stroke="#6B7280" />
                         <YAxis stroke="#6B7280" />
                         <Tooltip />
                         <Bar dataKey="stock" fill="#3B82F6" />
                         <Bar dataKey="received" fill="#10B981" />
                       </RechartsBarChart>
                     </ResponsiveContainer>
                   </div>

                   {/* Pie Chart */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Chart 3 - Pie Chart</h3>
                     <ResponsiveContainer width="100%" height={200}>
                       <RechartsPieChart>
                         <Pie
                           data={categoryData}
                           cx="50%"
                           cy="50%"
                           labelLine={false}
                           label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                           outerRadius={60}
                           fill="#8884d8"
                           dataKey="value"
                         >
                           {categoryData.map((entry, index) => (
                             <Cell key={`cell-${index}`} fill={entry.color} />
                           ))}
                         </Pie>
                         <Tooltip />
                       </RechartsPieChart>
                     </ResponsiveContainer>
                   </div>

                   {/* Area Chart */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Chart 4 - Area Chart</h3>
                     <ResponsiveContainer width="100%" height={200}>
                       <AreaChart data={chartData}>
                         <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                         <XAxis dataKey="month" stroke="#6B7280" />
                         <YAxis stroke="#6B7280" />
                         <Tooltip />
                         <Area type="monotone" dataKey="stock" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                         <Area type="monotone" dataKey="received" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                       </AreaChart>
                     </ResponsiveContainer>
                   </div>
                 </div>
               </div>

               {/* Stock Level Visualizations */}
               <div className="space-y-6">
                 <h2 className="text-2xl font-bold text-gray-900">5. Stock Level Visualizations</h2>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                   {/* Progress Bar */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Stock Level 1 - Progress Bar</h3>
                     <div className="space-y-4">
                       <div>
                         <div className="flex justify-between text-sm mb-1">
                           <span className="text-gray-600">Coffee Beans</span>
                           <span className="text-gray-900">75%</span>
                         </div>
                         <div className="w-full bg-gray-200 rounded-full h-2">
                           <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                         </div>
                       </div>
                       
                       <div>
                         <div className="flex justify-between text-sm mb-1">
                           <span className="text-gray-600">Milk</span>
                           <span className="text-gray-900">10%</span>
                         </div>
                         <div className="w-full bg-gray-200 rounded-full h-2">
                           <div className="bg-red-500 h-2 rounded-full" style={{ width: '10%' }}></div>
                         </div>
                       </div>
                       
                       <div>
                         <div className="flex justify-between text-sm mb-1">
                           <span className="text-gray-600">Bread</span>
                           <span className="text-gray-900">45%</span>
                         </div>
                         <div className="w-full bg-gray-200 rounded-full h-2">
                           <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '45%' }}></div>
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Circular Progress */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Stock Level 2 - Circular</h3>
                     <div className="flex justify-center">
                       <div className="relative w-24 h-24">
                         <svg className="w-24 h-24 transform -rotate-90">
                           <circle
                             cx="48"
                             cy="48"
                             r="36"
                             stroke="currentColor"
                             strokeWidth="8"
                             fill="transparent"
                             className="text-gray-200"
                           />
                           <circle
                             cx="48"
                             cy="48"
                             r="36"
                             stroke="currentColor"
                             strokeWidth="8"
                             fill="transparent"
                             strokeDasharray={`${2 * Math.PI * 36}`}
                             strokeDashoffset={`${2 * Math.PI * 36 * (1 - 0.75)}`}
                             className="text-green-500"
                           />
                         </svg>
                         <div className="absolute inset-0 flex items-center justify-center">
                           <span className="text-lg font-semibold text-gray-900">75%</span>
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Gauge Chart */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Stock Level 3 - Gauge</h3>
                     <div className="flex justify-center">
                       <div className="relative w-24 h-24">
                         <svg className="w-24 h-24 transform -rotate-90">
                           <path
                             d="M 48 12 A 36 36 0 0 1 84 48"
                             stroke="currentColor"
                             strokeWidth="8"
                             fill="transparent"
                             className="text-gray-200"
                           />
                           <path
                             d="M 48 12 A 36 36 0 0 1 84 48"
                             stroke="currentColor"
                             strokeWidth="8"
                             fill="transparent"
                             strokeDasharray={`${Math.PI * 36}`}
                             strokeDashoffset={`${Math.PI * 36 * (1 - 0.6)}`}
                             className="text-blue-500"
                           />
                         </svg>
                         <div className="absolute inset-0 flex items-center justify-center">
                           <span className="text-lg font-semibold text-gray-900">60%</span>
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Stacked Bar */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Stock Level 4 - Stacked</h3>
                     <div className="space-y-3">
                       <div className="flex items-center space-x-2">
                         <div className="w-16 h-8 bg-green-500 rounded"></div>
                         <div className="w-8 h-8 bg-yellow-500 rounded"></div>
                         <div className="w-4 h-8 bg-red-500 rounded"></div>
                         <span className="text-sm text-gray-600">Item A</span>
                       </div>
                       <div className="flex items-center space-x-2">
                         <div className="w-12 h-8 bg-green-500 rounded"></div>
                         <div className="w-12 h-8 bg-yellow-500 rounded"></div>
                         <div className="w-4 h-8 bg-red-500 rounded"></div>
                         <span className="text-sm text-gray-600">Item B</span>
                       </div>
                       <div className="flex items-center space-x-2">
                         <div className="w-4 h-8 bg-green-500 rounded"></div>
                         <div className="w-8 h-8 bg-yellow-500 rounded"></div>
                         <div className="w-16 h-8 bg-red-500 rounded"></div>
                         <span className="text-sm text-gray-600">Item C</span>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>

               {/* Status Indicators */}
               <div className="space-y-6">
                 <h2 className="text-2xl font-bold text-gray-900">6. Status Indicators</h2>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                   {/* Status Badges */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Status 1 - Badges</h3>
                     <div className="space-y-3">
                       <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                         <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                         In Stock
                       </span>
                       <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                         <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                         Low Stock
                       </span>
                       <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                         <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                         Out of Stock
                       </span>
                       <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                         <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                         In Transit
                       </span>
                     </div>
                   </div>

                   {/* Status Cards */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Status 2 - Cards</h3>
                     <div className="space-y-3">
                       <div className="flex items-center p-3 bg-green-50 rounded-lg">
                         <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                         <div>
                           <p className="text-sm font-medium text-green-900">Available</p>
                           <p className="text-xs text-green-600">150 units in stock</p>
                         </div>
                       </div>
                       <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                         <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3" />
                         <div>
                           <p className="text-sm font-medium text-yellow-900">Low Stock</p>
                           <p className="text-xs text-yellow-600">15 units remaining</p>
                         </div>
                       </div>
                       <div className="flex items-center p-3 bg-red-50 rounded-lg">
                         <X className="w-5 h-5 text-red-600 mr-3" />
                         <div>
                           <p className="text-sm font-medium text-red-900">Out of Stock</p>
                           <p className="text-xs text-red-600">0 units available</p>
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Status Icons */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Status 3 - Icons</h3>
                     <div className="grid grid-cols-2 gap-4">
                       <div className="text-center">
                         <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                           <CheckCircle className="w-6 h-6 text-green-600" />
                         </div>
                         <p className="text-xs text-gray-600">Active</p>
                       </div>
                       <div className="text-center">
                         <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                           <Clock className="w-6 h-6 text-yellow-600" />
                         </div>
                         <p className="text-xs text-gray-600">Pending</p>
                       </div>
                       <div className="text-center">
                         <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                           <X className="w-6 h-6 text-red-600" />
                         </div>
                         <p className="text-xs text-gray-600">Inactive</p>
                       </div>
                       <div className="text-center">
                         <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                           <Truck className="w-6 h-6 text-blue-600" />
                         </div>
                         <p className="text-xs text-gray-600">Shipping</p>
                       </div>
                     </div>
                   </div>

                   {/* Status Timeline */}
                   <div className="bg-white rounded-xl border border-gray-200 p-6">
                     <h3 className="text-lg font-semibold mb-4">Status 4 - Timeline</h3>
                     <div className="space-y-3">
                       <div className="flex items-center">
                         <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                         <div className="flex-1">
                           <p className="text-sm font-medium text-gray-900">Order Placed</p>
                           <p className="text-xs text-gray-500">2 hours ago</p>
                         </div>
                       </div>
                       <div className="flex items-center">
                         <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                         <div className="flex-1">
                           <p className="text-sm font-medium text-gray-900">Processing</p>
                           <p className="text-xs text-gray-500">1 hour ago</p>
                         </div>
                       </div>
                       <div className="flex items-center">
                         <div className="w-3 h-3 bg-gray-300 rounded-full mr-3"></div>
                         <div className="flex-1">
                           <p className="text-sm font-medium text-gray-400">Shipped</p>
                           <p className="text-xs text-gray-400">Pending</p>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>

                               {/* Advanced UI Components */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">7. Advanced UI Components</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Notification Cards */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">Notification 1</h3>
                      <div className="space-y-3">
                        <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                          <Bell className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">New Order</p>
                            <p className="text-xs text-blue-600">Order #12345 has been placed</p>
                          </div>
                        </div>
                        <div className="flex items-start p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-900">Stock Updated</p>
                            <p className="text-xs text-green-600">Inventory levels have been updated</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Data Tables */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">Data Table 1</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 font-medium text-gray-900">Item</th>
                              <th className="text-left py-2 font-medium text-gray-900">Stock</th>
                              <th className="text-left py-2 font-medium text-gray-900">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-900">Coffee Beans</td>
                              <td className="py-2 text-gray-600">150</td>
                              <td className="py-2">
                                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">In Stock</span>
                              </td>
                            </tr>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-gray-900">Milk</td>
                              <td className="py-2 text-gray-600">15</td>
                              <td className="py-2">
                                <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">Low Stock</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Modal-like Cards */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">Modal 1</h3>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">Confirm Action</h4>
                          <button className="text-gray-400 hover:text-gray-600">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this item?</p>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
                          <button className="px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Cancel</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modern Creative Cards - NextUI Inspired */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">8. Modern Creative Cards (NextUI Style)</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Gradient Card with Blur */}
                    <motion.div 
                      whileHover={{ scale: 1.02, y: -5 }}
                      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 p-8 text-white shadow-xl"
                    >
                      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
                      <div className="relative z-10">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                          <Package className="h-6 w-6" />
                        </div>
                        <h3 className="mb-2 text-xl font-bold">Inventory Overview</h3>
                        <p className="text-white/80">Real-time stock monitoring with AI insights</p>
                      </div>
                    </motion.div>

                    {/* Glassmorphism Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="group relative overflow-hidden rounded-2xl bg-gray-900/80 backdrop-blur-xl border border-gray-700 p-6 shadow-2xl"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <div className="relative z-10">
                        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
                          <TrendingUp className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-white">Analytics Dashboard</h3>
                        <p className="text-gray-200 text-sm">Advanced reporting and insights</p>
                      </div>
                    </motion.div>

                    {/* Neumorphism Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="rounded-2xl bg-gray-100 p-6 shadow-[inset_-12px_-12px_24px_rgba(255,255,255,0.6),inset_12px_12px_24px_rgba(174,174,192,0.4)]"
                    >
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg">
                        <BarChart3 className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-800">Performance Metrics</h3>
                      <p className="text-gray-600 text-sm">Track key performance indicators</p>
                    </motion.div>
                  </div>
                </div>

                {/* Aceternity UI Inspired Components */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">9. Aceternity UI Inspired Components</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Animated Border Card */}
                    <div className="group relative rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="relative z-10">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600">
                          <Sparkles className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900 group-hover:text-white transition-colors">Smart Features</h3>
                        <p className="text-gray-600 group-hover:text-white/80 transition-colors text-sm">AI-powered inventory optimization</p>
                      </div>
                    </div>

                    {/* Spotlight Card */}
                    <div className="group relative overflow-hidden rounded-2xl bg-gray-900 p-6 text-white">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                      <div className="relative z-10">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                          <Zap className="h-6 w-6" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold">Lightning Fast</h3>
                        <p className="text-gray-200 text-sm">Instant updates and real-time sync</p>
                      </div>
                    </div>

                    {/* Floating Card */}
                    <motion.div 
                      whileHover={{ y: -10 }}
                      className="relative rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 p-6 text-white shadow-2xl"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 to-cyan-500/20 rounded-2xl"></div>
                      <div className="relative z-10">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                          <Leaf className="h-6 w-6" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold">Eco-Friendly</h3>
                        <p className="text-gray-100 text-sm">Sustainable inventory management</p>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Shadcn/UI Inspired Components */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">10. Shadcn/UI Inspired Components</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Card with Badge */}
                    <div className="group relative overflow-hidden rounded-lg border bg-white text-gray-900 shadow-sm">
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <div className="mr-1 h-1.5 w-1.5 rounded-full bg-green-400"></div>
                          Active
                        </span>
                      </div>
                      <div className="p-6">
                        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                          <Database className="h-5 w-5 text-blue-600" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold leading-none tracking-tight">Data Management</h3>
                        <p className="text-sm text-gray-600">Centralized data storage and retrieval</p>
                      </div>
                    </div>

                    {/* Card with Avatar */}
                    <div className="rounded-lg border bg-white text-gray-900 shadow-sm">
                      <div className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <User className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">User Management</h3>
                            <p className="text-sm text-gray-600">Role-based access control</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card with Progress */}
                    <div className="rounded-lg border bg-white text-gray-900 shadow-sm">
                      <div className="p-6">
                        <div className="mb-4 flex items-center justify-between">
                          <h3 className="text-lg font-semibold">Storage Usage</h3>
                          <span className="text-sm text-gray-600">75%</span>
                        </div>
                        <div className="mb-4 h-2 w-full rounded-full bg-gray-200">
                          <div className="h-2 rounded-full bg-blue-600" style={{ width: '75%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600">750GB of 1TB used</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chakra UI Inspired Components */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">11. Chakra UI Inspired Components</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Stat Card */}
                    <div className="rounded-xl bg-white p-6 shadow-lg border border-gray-200">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500">
                            <DollarSign className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                          <p className="text-2xl font-bold text-gray-900">$45,231</p>
                          <p className="text-sm text-green-600">+20.1% from last month</p>
                        </div>
                      </div>
                    </div>

                    {/* Alert Card */}
                    <div className="rounded-xl border-l-4 border-yellow-400 bg-yellow-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">Low Stock Alert</h3>
                          <p className="mt-1 text-sm text-yellow-700">5 items are running low on stock</p>
                        </div>
                      </div>
                    </div>

                    {/* Simple Card */}
                    <div className="rounded-xl bg-white p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                          <p className="text-sm text-gray-500">Common tasks and shortcuts</p>
                        </div>
                        <div className="flex space-x-2">
                          <button className="rounded-lg bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600">
                            Add Item
                          </button>
                          <button className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            Export
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Material UI Inspired Components */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">12. Material UI Inspired Components</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Elevated Card */}
                    <motion.div 
                      whileHover={{ y: -8 }}
                      className="rounded-lg bg-white p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
                    >
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-500 shadow-lg">
                        <Settings className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-gray-900">System Settings</h3>
                      <p className="text-sm text-gray-600">Configure system preferences and options</p>
                    </motion.div>

                    {/* Outlined Card */}
                    <div className="rounded-lg border-2 border-gray-200 bg-white p-6 hover:border-blue-500 transition-colors">
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                        <Shield className="h-6 w-6 text-gray-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-gray-900">Security</h3>
                      <p className="text-sm text-gray-600">Advanced security and privacy settings</p>
                    </div>

                    {/* Filled Card */}
                    <div className="rounded-lg bg-blue-500 p-6 text-white shadow-lg">
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                        <Award className="h-6 w-6" />
                      </div>
                      <h3 className="mb-2 text-lg font-medium">Premium Features</h3>
                      <p className="text-sm text-blue-100">Unlock advanced functionality</p>
                    </div>
                  </div>
                </div>

                {/* Creative Solid Color Tags */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">13. Creative Solid Color Tags</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Gradient Tags */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Gradient Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                          Primary
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-400 to-blue-500 text-white">
                          Success
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500 to-red-500 text-white">
                          Warning
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                          Alert
                        </span>
                      </div>
                    </div>

                    {/* Solid Color Tags */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Solid Colors</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white shadow-lg">
                          Indigo
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-600 text-white shadow-lg">
                          Emerald
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-rose-600 text-white shadow-lg">
                          Rose
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-600 text-white shadow-lg">
                          Amber
                        </span>
                      </div>
                    </div>

                    {/* Outlined Tags */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Outlined Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-2 border-blue-500 text-blue-600 bg-blue-50">
                          Outlined
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-2 border-green-500 text-green-600 bg-green-50">
                          Success
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-2 border-red-500 text-red-600 bg-red-50">
                          Error
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-2 border-yellow-500 text-yellow-600 bg-yellow-50">
                          Warning
                        </span>
                      </div>
                    </div>

                    {/* Animated Tags */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Animated Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        <motion.span 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-600 text-white cursor-pointer"
                        >
                          Hover Me
                        </motion.span>
                        <motion.span 
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-pink-600 text-white"
                        >
                          Pulsing
                        </motion.span>
                        <motion.span 
                          whileHover={{ rotate: 5 }}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-600 text-white cursor-pointer"
                        >
                          Rotate
                        </motion.span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Advanced Stock Visualizations */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">14. Advanced Stock Visualizations</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* 3D Progress Bar */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">3D Progress Bar</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Coffee Beans</span>
                            <span className="text-gray-900 font-medium">75%</span>
                          </div>
                          <div className="relative h-4 bg-gray-200 rounded-lg overflow-hidden shadow-inner">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 rounded-lg shadow-lg transform -skew-x-12" style={{ width: '75%' }}></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-green-300 to-green-500 rounded-lg opacity-50" style={{ width: '75%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Animated Circular Progress */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">Animated Circular</h3>
                      <div className="flex justify-center">
                        <div className="relative w-24 h-24">
                          <svg className="w-24 h-24 transform -rotate-90">
                            <circle
                              cx="48"
                              cy="48"
                              r="36"
                              stroke="currentColor"
                              strokeWidth="8"
                              fill="transparent"
                              className="text-gray-200"
                            />
                            <motion.circle
                              cx="48"
                              cy="48"
                              r="36"
                              stroke="currentColor"
                              strokeWidth="8"
                              fill="transparent"
                              strokeDasharray={`${2 * Math.PI * 36}`}
                              strokeDashoffset={`${2 * Math.PI * 36 * (1 - 0.75)}`}
                              className="text-blue-500"
                              initial={{ strokeDashoffset: `${2 * Math.PI * 36}` }}
                              animate={{ strokeDashoffset: `${2 * Math.PI * 36 * (1 - 0.75)}` }}
                              transition={{ duration: 2, ease: "easeOut" }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-semibold text-gray-900">75%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Liquid Fill Animation */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">Liquid Fill</h3>
                      <div className="flex justify-center">
                        <div className="relative w-24 h-24 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            className="absolute bottom-0 w-full bg-gradient-to-t from-blue-500 to-blue-400"
                            initial={{ height: "0%" }}
                            animate={{ height: "75%" }}
                            transition={{ duration: 2, ease: "easeOut" }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-semibold text-gray-900 z-10">75%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Wave Animation */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold mb-4">Wave Animation</h3>
                      <div className="flex justify-center">
                        <div className="relative w-24 h-24 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            className="absolute bottom-0 w-full bg-blue-500"
                            initial={{ height: "0%" }}
                            animate={{ height: "75%" }}
                            transition={{ duration: 2, ease: "easeOut" }}
                          >
                            <motion.div
                              className="absolute top-0 w-full h-4 bg-blue-400 rounded-full"
                              animate={{ y: [0, -10, 0] }}
                              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            />
                          </motion.div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-semibold text-gray-900 z-10">75%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                                {/* Advanced Animations */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">15. Advanced Animations</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Morphing Card */}
                    <motion.div 
                      whileHover={{ 
                        scale: 1.05,
                        rotateY: 10,
                        rotateX: 5
                      }}
                      className="rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 p-6 text-white shadow-xl"
                    >
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                        <Sparkles className="h-6 w-6" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold">Morphing Card</h3>
                      <p className="text-gray-100 text-sm">3D transform on hover</p>
                    </motion.div>

                    {/* Floating Animation */}
                    <motion.div 
                      animate={{ 
                        y: [0, -10, 0],
                        rotate: [0, 2, -2, 0]
                      }}
                      transition={{ 
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 p-6 text-white shadow-xl"
                    >
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                        <Waves className="h-6 w-6" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold">Floating Card</h3>
                      <p className="text-gray-100 text-sm">Continuous floating animation</p>
                    </motion.div>

                    {/* Pulse Glow */}
                    <motion.div 
                      animate={{ 
                        boxShadow: [
                          "0 0 0 0 rgba(59, 130, 246, 0.4)",
                          "0 0 0 20px rgba(59, 130, 246, 0)",
                          "0 0 0 0 rgba(59, 130, 246, 0)"
                        ]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeOut"
                      }}
                      className="rounded-xl bg-blue-500 p-6 text-white"
                    >
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                        <Zap className="h-6 w-6" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold">Pulse Glow</h3>
                      <p className="text-gray-100 text-sm">Pulsing glow effect</p>
                    </motion.div>
                  </div>
                </div>

                {/* Advanced Charts & Analytics */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">16. Advanced Charts & Analytics</h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Composed Chart */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
                      <h3 className="text-lg font-semibold mb-4">Advanced Analytics - Composed Chart</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="month" stroke="#6B7280" />
                          <YAxis stroke="#6B7280" />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#1F2937',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#F9FAFB'
                            }}
                          />
                          <Legend />
                          <Bar dataKey="stock" fill="#3B82F6" opacity={0.8} />
                          <Line type="monotone" dataKey="received" stroke="#10B981" strokeWidth={3} />
                          <Area type="monotone" dataKey="ordered" fill="#8B5CF6" fillOpacity={0.3} stroke="#8B5CF6" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Radar Chart */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
                      <h3 className="text-lg font-semibold mb-4">Performance Metrics - Radar Chart</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={[
                              { name: 'Efficiency', value: 85, color: '#3B82F6' },
                              { name: 'Accuracy', value: 92, color: '#10B981' },
                              { name: 'Speed', value: 78, color: '#F59E0B' },
                              { name: 'Quality', value: 88, color: '#8B5CF6' },
                              { name: 'Reliability', value: 95, color: '#EF4444' }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {[
                              { name: 'Efficiency', value: 85, color: '#3B82F6' },
                              { name: 'Accuracy', value: 92, color: '#10B981' },
                              { name: 'Speed', value: 78, color: '#F59E0B' },
                              { name: 'Quality', value: 88, color: '#8B5CF6' },
                              { name: 'Reliability', value: 95, color: '#EF4444' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#1F2937',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#F9FAFB'
                            }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Interactive Data Visualizations */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">17. Interactive Data Visualizations</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Animated Donut Chart */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Animated Donut Chart</h3>
                      <div className="flex justify-center">
                        <div className="relative w-32 h-32">
                          <motion.svg 
                            className="w-32 h-32 transform -rotate-90"
                            animate={{ 
                              rotate: [0, 2, 0, -2, 0]
                            }}
                            transition={{
                              duration: 6,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <motion.circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="transparent"
                              className="text-gray-200"
                              animate={{ 
                                opacity: [0.3, 0.6, 0.3]
                              }}
                              transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            />
                            <motion.circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="transparent"
                              strokeDasharray="352"
                              className="text-blue-500"
                              initial={{ strokeDashoffset: 352 }}
                              animate={{ 
                                strokeDashoffset: [352, 113, 352],
                                strokeWidth: [12, 14, 12]
                              }}
                              transition={{
                                duration: 2,
                                ease: "easeInOut",
                                repeat: Infinity,
                                repeatType: "reverse"
                              }}
                            />
                          </motion.svg>
                          <motion.div 
                            className="absolute inset-0 flex flex-col items-center justify-center"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                          >
                            <motion.span 
                              className="text-2xl font-bold text-gray-900"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ 
                                opacity: 1, 
                                scale: [1, 1.05, 1],
                                textShadow: [
                                  "0 0 0px rgba(59, 130, 246, 0)",
                                  "0 0 10px rgba(59, 130, 246, 0.5)",
                                  "0 0 0px rgba(59, 130, 246, 0)"
                                ]
                              }}
                              transition={{ 
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            >
                              68%
                            </motion.span>
                            <motion.span 
                              className="text-sm text-gray-600"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.5, delay: 1.2 }}
                            >
                              Complete
                            </motion.span>
                          </motion.div>
                        </div>
                      </div>

                    </motion.div>

                    {/* Gradient Progress Ring */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Gradient Progress Ring</h3>
                      <div className="flex justify-center">
                        <div className="relative w-32 h-32">
                          <motion.svg 
                            className="w-32 h-32 transform -rotate-90"
                            animate={{ 
                              rotate: [0, -3, 0, 3, 0]
                            }}
                            transition={{
                              duration: 8,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <defs>
                              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#3B82F6" />
                                <stop offset="50%" stopColor="#8B5CF6" />
                                <stop offset="100%" stopColor="#EC4899" />
                              </linearGradient>
                            </defs>
                            <motion.circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="transparent"
                              className="text-gray-200"
                              animate={{ 
                                opacity: [0.3, 0.6, 0.3]
                              }}
                              transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            />
                            <motion.circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="url(#gradient)"
                              strokeWidth="12"
                              fill="transparent"
                              strokeDasharray="352"
                              className="text-blue-500"
                              initial={{ strokeDashoffset: 352 }}
                              animate={{ 
                                strokeDashoffset: [352, 63, 352],
                                strokeWidth: [12, 16, 12]
                              }}
                              transition={{
                                duration: 2.5,
                                ease: "easeInOut",
                                repeat: Infinity,
                                repeatType: "reverse"
                              }}
                            />
                          </motion.svg>
                          <motion.div 
                            className="absolute inset-0 flex flex-col items-center justify-center"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                          >
                            <motion.span 
                              className="text-2xl font-bold text-gray-900"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ 
                                opacity: 1, 
                                scale: [1, 1.05, 1],
                                textShadow: [
                                  "0 0 0px rgba(236, 72, 153, 0)",
                                  "0 0 10px rgba(236, 72, 153, 0.5)",
                                  "0 0 0px rgba(236, 72, 153, 0)"
                                ]
                              }}
                              transition={{ 
                                duration: 2.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            >
                              82%
                            </motion.span>
                            <motion.span 
                              className="text-sm text-gray-600"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.5, delay: 1.2 }}
                            >
                              Success
                            </motion.span>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Multi-Segment Progress */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Multi-Segment Progress</h3>
                      <div className="space-y-4">
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5 }}
                        >
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Processing</span>
                            <span className="text-gray-900 font-medium">45%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <motion.div 
                              className="h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                              initial={{ width: "0%" }}
                              animate={{ width: "45%" }}
                              transition={{ 
                                duration: 2, 
                                ease: "easeOut",
                                repeat: Infinity,
                                repeatType: "reverse",
                                repeatDelay: 2
                              }}
                            />
                          </div>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                        >
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Quality Check</span>
                            <span className="text-gray-900 font-medium">78%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <motion.div 
                              className="h-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"
                              initial={{ width: "0%" }}
                              animate={{ width: "78%" }}
                              transition={{ 
                                duration: 2, 
                                ease: "easeOut", 
                                delay: 0.5,
                                repeat: Infinity,
                                repeatType: "reverse",
                                repeatDelay: 2
                              }}
                            />
                          </div>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: 0.4 }}
                        >
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Shipping</span>
                            <span className="text-gray-900 font-medium">23%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <motion.div 
                              className="h-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-full"
                              initial={{ width: "0%" }}
                              animate={{ width: "23%" }}
                              transition={{ 
                                duration: 2, 
                                ease: "easeOut", 
                                delay: 1,
                                repeat: Infinity,
                                repeatType: "reverse",
                                repeatDelay: 2
                              }}
                            />
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Animated Statistics */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Animated Statistics</h3>
                      <div className="space-y-4">
                        <motion.div 
                          className="text-center"
                          whileHover={{ scale: 1.05 }}
                        >
                          <motion.div 
                            className="text-3xl font-bold text-blue-600 mb-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              duration: 0.5, 
                              ease: "easeOut",
                              repeat: Infinity,
                              repeatType: "reverse",
                              repeatDelay: 3
                            }}
                          >
                            1,247
                          </motion.div>
                          <div className="text-sm text-gray-600">Total Items</div>
                        </motion.div>
                        <motion.div 
                          className="text-center"
                          whileHover={{ scale: 1.05 }}
                        >
                          <motion.div 
                            className="text-3xl font-bold text-green-600 mb-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              duration: 0.5, 
                              ease: "easeOut", 
                              delay: 0.2,
                              repeat: Infinity,
                              repeatType: "reverse",
                              repeatDelay: 3
                            }}
                          >
                            89.2%
                          </motion.div>
                          <div className="text-sm text-gray-600">Accuracy Rate</div>
                        </motion.div>
                        <motion.div 
                          className="text-center"
                          whileHover={{ scale: 1.05 }}
                        >
                          <motion.div 
                            className="text-3xl font-bold text-purple-600 mb-1"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              duration: 0.5, 
                              ease: "easeOut", 
                              delay: 0.4,
                              repeat: Infinity,
                              repeatType: "reverse",
                              repeatDelay: 3
                            }}
                          >
                            2.4s
                          </motion.div>
                          <div className="text-sm text-gray-600">Avg Response</div>
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Advanced Animated Charts */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">19. Advanced Animated Charts</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Pulsing Circle Chart */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Pulsing Circle Chart</h3>
                      <div className="flex justify-center">
                        <div className="relative w-32 h-32">
                          <motion.div
                            className="absolute inset-0 rounded-full bg-blue-500 opacity-20"
                            animate={{
                              scale: [1, 1.2, 1],
                              opacity: [0.2, 0.1, 0.2]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                          <motion.div
                            className="absolute inset-2 rounded-full bg-blue-400 opacity-30"
                            animate={{
                              scale: [1, 1.1, 1],
                              opacity: [0.3, 0.15, 0.3]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 0.5
                            }}
                          />
                          <motion.div
                            className="absolute inset-4 rounded-full bg-blue-300 opacity-40"
                            animate={{
                              scale: [1, 1.05, 1],
                              opacity: [0.4, 0.2, 0.4]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut",
                              delay: 1
                            }}
                          />
                          <div className="absolute inset-6 rounded-full bg-blue-600 flex items-center justify-center">
                            <span className="text-white font-bold text-lg">75%</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Animated Bar Chart */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Animated Bar Chart</h3>
                      <div className="flex items-end justify-center space-x-2 h-24">
                        {[0.3, 0.6, 0.8, 0.5, 0.9, 0.7].map((height, index) => (
                          <motion.div
                            key={index}
                            className="w-4 bg-gradient-to-t from-blue-500 to-blue-300 rounded-t"
                            initial={{ height: 0 }}
                            animate={{ height: `${height * 100}%` }}
                            transition={{
                              duration: 1,
                              delay: index * 0.1,
                              repeat: Infinity,
                              repeatType: "reverse",
                              repeatDelay: 2
                            }}
                          />
                        ))}
                      </div>
                    </motion.div>

                    {/* Rotating Donut Chart */}
                    <motion.div 
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <h3 className="text-lg font-semibold mb-4">Rotating Donut Chart</h3>
                      <div className="flex justify-center">
                        <motion.div 
                          className="w-32 h-32 relative"
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 10,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        >
                          <svg className="w-32 h-32 transform -rotate-90">
                            <circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="transparent"
                              className="text-gray-200"
                            />
                            <motion.circle
                              cx="64"
                              cy="64"
                              r="56"
                              stroke="currentColor"
                              strokeWidth="12"
                              fill="transparent"
                              strokeDasharray={`${2 * Math.PI * 56}`}
                              className="text-green-500"
                              initial={{ strokeDashoffset: `${2 * Math.PI * 56}` }}
                              animate={{ strokeDashoffset: `${2 * Math.PI * 56 * (1 - 0.75)}` }}
                              transition={{ 
                                duration: 2, 
                                ease: "easeOut",
                                repeat: Infinity,
                                repeatType: "reverse",
                                repeatDelay: 1
                              }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-2xl font-bold text-gray-900">75%</span>
                            <span className="text-sm text-gray-600">Complete</span>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Modern Dashboard Cards */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">18. Modern Dashboard Cards</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Metric Card with Trend */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                          <TrendingUp className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex items-center text-green-600 text-sm font-medium">
                          <TrendingUp className="h-4 w-4 mr-1" />
                          +12.5%
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">$45,231.89</p>
                        <p className="text-xs text-gray-500 mt-1">vs. $40,123 last month</p>
                      </div>
                    </div>

                    {/* Status Card with Icon */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          <span className="text-sm text-green-600 font-medium">Active</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">System Status</p>
                        <p className="text-2xl font-bold text-gray-900">All Systems</p>
                        <p className="text-xs text-gray-500 mt-1">Last updated 2 min ago</p>
                      </div>
                    </div>

                    {/* Alert Card */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-100">
                          <AlertTriangle className="h-6 w-6 text-yellow-600" />
                        </div>
                        <div className="flex items-center text-yellow-600 text-sm font-medium">
                          <AlertTriangle className="h-4 w-4 mr-1" />
                          Warning
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
                        <p className="text-2xl font-bold text-gray-900">5 Items</p>
                        <p className="text-xs text-gray-500 mt-1">Requires attention</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats Cards Section */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">19. Quick Stats Cards</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Modern Gradient Stats */}
                    <motion.div 
                      whileHover={{ scale: 1.05, y: -5 }}
                      className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white shadow-lg"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-white/20 rounded-lg">
                          <Package className="h-6 w-6" />
                        </div>
                        <motion.div 
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="text-2xl font-bold"
                        >
                          1,234
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold mb-1">Total Items</h3>
                      <p className="text-blue-100 text-sm">+12% from last month</p>
                    </motion.div>

                    {/* Glass Effect Stats */}
                    <div className="backdrop-blur-md bg-gray-900/80 border border-gray-700 rounded-xl p-6 shadow-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-500/20 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-green-300" />
                        </div>
                        <motion.div 
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="text-2xl font-bold text-white"
                        >
                          $45.2K
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">Revenue</h3>
                      <p className="text-green-200 text-sm">+8.5% increase</p>
                    </div>

                    {/* Neumorphic Stats */}
                    <div className="bg-gray-100 rounded-xl p-6 shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.06)] border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-orange-100 rounded-lg shadow-sm">
                          <AlertTriangle className="h-6 w-6 text-orange-600" />
                        </div>
                        <motion.div 
                          animate={{ color: ["#dc2626", "#f97316", "#dc2626"] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="text-2xl font-bold text-red-600"
                        >
                          23
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Low Stock</h3>
                      <p className="text-gray-600 text-sm">Requires attention</p>
                    </div>

                    {/* Animated Border Stats */}
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="relative bg-white rounded-xl p-6 shadow-lg border-2 border-transparent"
                      style={{
                        background: 'linear-gradient(white, white) padding-box, linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4) border-box'
                      }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-indigo-100 rounded-lg">
                          <Users className="h-6 w-6 text-indigo-600" />
                        </div>
                        <motion.div 
                          animate={{ 
                            background: [
                              "linear-gradient(45deg, #667eea, #764ba2)",
                              "linear-gradient(45deg, #764ba2, #667eea)"
                            ]
                          }}
                          transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
                          className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
                        >
                          156
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Active Users</h3>
                      <p className="text-gray-600 text-sm">+5 new today</p>
                    </motion.div>
                  </div>

                  {/* Second Row of Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Floating Stats Card */}
                    <motion.div 
                      whileHover={{ y: -10 }}
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 rounded-xl p-6 text-white shadow-2xl"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                          <Zap className="h-6 w-6" />
                        </div>
                        <motion.div 
                          animate={{ textShadow: ["0 0 0px rgba(255,255,255,0)", "0 0 20px rgba(255,255,255,0.8)", "0 0 0px rgba(255,255,255,0)"] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="text-2xl font-bold"
                        >
                          98.5%
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold mb-1">Uptime</h3>
                      <p className="text-pink-100 text-sm">Excellent performance</p>
                    </motion.div>

                    {/* Glowing Stats Card */}
                    <motion.div 
                      whileHover={{ scale: 1.05 }}
                      className="bg-gray-900 rounded-xl p-6 text-white shadow-lg relative overflow-hidden"
                    >
                      <motion.div 
                        animate={{ 
                          background: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 70%)"
                        }}
                        className="absolute inset-0"
                      />
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 bg-blue-500/20 rounded-lg">
                            <Database className="h-6 w-6 text-blue-400" />
                          </div>
                          <motion.div 
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="text-2xl font-bold"
                          >
                            2.4TB
                          </motion.div>
                        </div>
                        <h3 className="text-lg font-semibold mb-1">Storage Used</h3>
                        <p className="text-blue-200 text-sm">75% of capacity</p>
                      </div>
                    </motion.div>

                    {/* Minimalist Stats Card */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-500 transition-colors">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gray-100 rounded-lg">
                          <Clock className="h-6 w-6 text-gray-600" />
                        </div>
                        <motion.div 
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="text-2xl font-bold text-gray-900"
                        >
                          24/7
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Support</h3>
                      <p className="text-gray-600 text-sm">Always available</p>
                    </div>
                  </div>
                </div>

                {/* Quick Action Buttons Section */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">20. Quick Action Buttons</h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {/* Primary Action Buttons */}
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
                    >
                      <Plus className="h-5 w-5 mx-auto mb-1" />
                      <span className="text-sm">Add Item</span>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors shadow-lg"
                    >
                      <Upload className="h-5 w-5 mx-auto mb-1" />
                      <span className="text-sm">Import</span>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors shadow-lg"
                    >
                      <Download className="h-5 w-5 mx-auto mb-1" />
                      <span className="text-sm">Export</span>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 transition-colors shadow-lg"
                    >
                      <RefreshCw className="h-5 w-5 mx-auto mb-1" />
                      <span className="text-sm">Refresh</span>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors shadow-lg"
                    >
                      <Trash2 className="h-5 w-5 mx-auto mb-1" />
                      <span className="text-sm">Delete</span>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
                    >
                      <Settings className="h-5 w-5 mx-auto mb-1" />
                      <span className="text-sm">Settings</span>
                    </motion.button>
                  </div>

                  {/* Glass Effect Action Buttons */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <motion.button 
                      whileHover={{ scale: 1.05, y: -2 }}
                      className="backdrop-blur-md bg-gray-900/80 border border-gray-700 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Search className="h-5 w-5" />
                        <span>Search</span>
                      </div>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05, y: -2 }}
                      className="backdrop-blur-md bg-gray-900/80 border border-gray-700 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Filter className="h-5 w-5" />
                        <span>Filter</span>
                      </div>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05, y: -2 }}
                      className="backdrop-blur-md bg-gray-900/80 border border-gray-700 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <BarChart3 className="h-5 w-5" />
                        <span>Analytics</span>
                      </div>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05, y: -2 }}
                      className="backdrop-blur-md bg-gray-900/80 border border-gray-700 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Bell className="h-5 w-5" />
                        <span>Notifications</span>
                      </div>
                    </motion.button>
                  </div>

                  {/* Gradient Action Buttons */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <motion.button 
                      whileHover={{ scale: 1.05, rotate: 2 }}
                      className="bg-gradient-to-r from-pink-500 to-violet-500 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Heart className="h-5 w-5" />
                        <span>Favorites</span>
                      </div>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05, rotate: -2 }}
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Star className="h-5 w-5" />
                        <span>Star</span>
                      </div>
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.05, rotate: 2 }}
                      className="bg-gradient-to-r from-green-400 to-blue-500 text-white px-6 py-3 rounded-xl shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <Zap className="h-5 w-5" />
                        <span>Quick Action</span>
                      </div>
                    </motion.button>
                  </div>

                  {/* Floating Action Buttons */}
                  <div className="flex flex-wrap gap-4">
                    <motion.button 
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-red-500 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
                    >
                      <Plus className="h-6 w-6" />
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-blue-500 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
                    >
                      <Edit className="h-6 w-6" />
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-green-500 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
                    >
                      <CheckCircle className="h-6 w-6" />
                    </motion.button>

                    <motion.button 
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-purple-500 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
                    >
                      <Eye className="h-6 w-6" />
                    </motion.button>
                  </div>
                </div>

                {/* Inventory Item Cards Section */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">21. Inventory Item Cards</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Modern Item Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02, y: -5 }}
                      className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                          <Package className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          <span className="text-sm text-green-600 font-medium">In Stock</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Coffee Beans</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">SKU:</span>
                          <span className="font-medium">INV-001</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Quantity:</span>
                          <span className="font-medium text-green-600">150 kg</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Price:</span>
                          <span className="font-medium">$45.00</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors">
                          Edit
                        </button>
                        <button className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-200 transition-colors">
                          View
                        </button>
                      </div>
                    </motion.div>

                    {/* Glass Effect Item Card */}
                    <div className="backdrop-blur-md bg-gray-900/80 border border-gray-700 rounded-xl p-6 shadow-xl">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/20">
                          <Package className="h-6 w-6 text-orange-300" />
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 animate-pulse"></div>
                          <span className="text-sm text-yellow-300 font-medium">Low Stock</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">Organic Milk</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-300">SKU:</span>
                          <span className="font-medium text-white">INV-002</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-300">Quantity:</span>
                          <span className="font-medium text-yellow-300">15 liters</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-300">Price:</span>
                          <span className="font-medium text-white">$6.50</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="flex-1 bg-gray-800/80 text-white px-3 py-2 rounded-lg text-sm hover:bg-gray-700/80 transition-colors border border-gray-600">
                          Edit
                        </button>
                        <button className="flex-1 bg-gray-800/80 text-white px-3 py-2 rounded-lg text-sm hover:bg-gray-700/80 transition-colors border border-gray-600">
                          View
                        </button>
                      </div>
                    </div>

                    {/* Gradient Item Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-6 text-white shadow-lg"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/20">
                          <Package className="h-6 w-6" />
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-300 rounded-full mr-2"></div>
                          <span className="text-sm text-green-200 font-medium">Available</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Artisan Bread</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-purple-200">SKU:</span>
                          <span className="font-medium">INV-003</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-purple-200">Quantity:</span>
                          <span className="font-medium text-green-200">75 units</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-purple-200">Price:</span>
                          <span className="font-medium">$3.99</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="flex-1 bg-white/20 text-white px-3 py-2 rounded-lg text-sm hover:bg-white/30 transition-colors">
                          Edit
                        </button>
                        <button className="flex-1 bg-white/20 text-white px-3 py-2 rounded-lg text-sm hover:bg-white/30 transition-colors">
                          View
                        </button>
                      </div>
                    </motion.div>
                  </div>

                  {/* Second Row of Item Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Minimalist Item Card */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                          <Package className="h-5 w-5 text-gray-600" />
                        </div>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">In Stock</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Fresh Vegetables</h3>
                      <div className="space-y-1 mb-4 text-sm text-gray-600">
                        <div>SKU: INV-004</div>
                        <div>Quantity: 200 kg</div>
                        <div>Price: $2.50/kg</div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors">
                          Edit
                        </button>
                        <button className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 transition-colors">
                          View
                        </button>
                      </div>
                    </div>

                    {/* Animated Item Card */}
                    <motion.div 
                      whileHover={{ scale: 1.02, rotate: 1 }}
                      className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl p-6 text-white shadow-lg"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <motion.div 
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                          className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/20"
                        >
                          <Package className="h-6 w-6" />
                        </motion.div>
                        <motion.div 
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="flex items-center"
                        >
                          <div className="w-2 h-2 bg-green-300 rounded-full mr-2"></div>
                          <span className="text-sm text-green-200 font-medium">Active</span>
                        </motion.div>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Electronics</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-200">SKU:</span>
                          <span className="font-medium">INV-005</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-200">Quantity:</span>
                          <span className="font-medium text-green-200">45 units</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-200">Price:</span>
                          <span className="font-medium">$299.99</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="flex-1 bg-white/20 text-white px-3 py-2 rounded-lg text-sm hover:bg-white/30 transition-colors">
                          Edit
                        </button>
                        <button className="flex-1 bg-white/20 text-white px-3 py-2 rounded-lg text-sm hover:bg-white/30 transition-colors">
                          View
                        </button>
                      </div>
                    </motion.div>

                    {/* Neumorphic Item Card */}
                    <div className="bg-gray-100 rounded-xl p-6 shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.06)] border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm">
                          <Package className="h-6 w-6 text-gray-600" />
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                          <span className="text-sm text-red-600 font-medium">Out of Stock</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Wine</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">SKU:</span>
                          <span className="font-medium">INV-006</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Quantity:</span>
                          <span className="font-medium text-red-600">0 bottles</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Price:</span>
                          <span className="font-medium">$89.99</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-300 transition-colors">
                          Edit
                        </button>
                        <button className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-300 transition-colors">
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabs Bar Examples Section */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">22. Tabs Bar Examples</h2>
                  
                  {/* Modern Tabs */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Modern Tabs</h3>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                        {['Overview', 'Analytics', 'Reports', 'Settings'].map((tab, index) => (
                          <motion.button
                            key={tab}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                              index === 0 ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {tab}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Gradient Tabs */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Gradient Tabs</h3>
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6">
                      <div className="flex space-x-1 bg-white/20 backdrop-blur-sm rounded-lg p-1">
                        {['Dashboard', 'Inventory', 'Orders', 'Users'].map((tab, index) => (
                          <motion.button
                            key={tab}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                              index === 1 ? 'bg-white text-purple-600 shadow-sm' : 'text-white hover:text-purple-100'
                            }`}
                          >
                            {tab}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Glass Effect Tabs */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Glass Effect Tabs</h3>
                    <div className="backdrop-blur-md bg-gray-900/80 border border-gray-700 rounded-xl p-6">
                      <div className="flex space-x-1 bg-gray-800/50 backdrop-blur-sm rounded-lg p-1">
                        {['Home', 'Products', 'Customers', 'Analytics'].map((tab, index) => (
                          <motion.button
                            key={tab}
                            whileHover={{ scale: 1.05, y: -1 }}
                            whileTap={{ scale: 0.95 }}
                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                              index === 2 ? 'bg-white/20 text-white shadow-sm' : 'text-white/80 hover:text-white'
                            }`}
                          >
                            {tab}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Animated Tabs */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Animated Tabs</h3>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 relative">
                        {['Tab 1', 'Tab 2', 'Tab 3', 'Tab 4'].map((tab, index) => (
                          <motion.button
                            key={tab}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 relative z-10 ${
                              index === 3 ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {tab}
                          </motion.button>
                        ))}
                        <motion.div
                          className="absolute top-1 left-1 w-1/4 h-[calc(100%-8px)] bg-white rounded-md shadow-sm"
                          initial={{ x: 0 }}
                          animate={{ x: "300%" }}
                          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Minimalist Tabs */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Minimalist Tabs</h3>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex border-b border-gray-200">
                        {['Overview', 'Details', 'History', 'Settings'].map((tab, index) => (
                          <motion.button
                            key={tab}
                            whileHover={{ y: -1 }}
                            className={`px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                              index === 0 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {tab}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Floating Tabs */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Floating Tabs</h3>
                    <div className="bg-gray-900 rounded-xl p-6">
                      <div className="flex space-x-2 bg-gray-800 rounded-xl p-2">
                        {['Home', 'Store', 'Cart', 'Profile'].map((tab, index) => (
                          <motion.button
                            key={tab}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              index === 1 ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            {tab}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inventory Item Cards Section */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">23. Inventory Item Cards</h2>
                  
                  {/* Modern Card Design */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Modern Card Design</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={item.id}
                          whileHover={{ y: -8, scale: 1.02 }}
                          className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900 text-lg mb-1">{item.name}</h3>
                              <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                              <div className="flex items-center mt-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                                  item.status === 'low-stock' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {item.status.replace('-', ' ').toUpperCase()}
                                </span>
                                <span className="ml-2 text-xs text-gray-500">{item.category}</span>
                              </div>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                              <Package className="w-6 h-6 text-white" />
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Current Stock</span>
                              <span className="font-bold text-gray-900">{item.quantity} {item.unit}</span>
                            </div>
                            
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Stock Level</span>
                                <span className="text-gray-900">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <motion.div 
                                  className={`h-2 rounded-full ${
                                    item.quantity < item.minStock ? 'bg-red-500' :
                                    item.quantity < item.maxStock * 0.3 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center p-2 bg-blue-100 rounded-lg">
                                <p className="font-bold text-blue-900">{item.inTransit}</p>
                                <p className="text-xs text-blue-700">In Transit</p>
                              </div>
                              <div className="text-center p-2 bg-yellow-100 rounded-lg">
                                <p className="font-bold text-yellow-900">{item.ordered}</p>
                                <p className="text-xs text-yellow-700">Ordered</p>
                              </div>
                              <div className="text-center p-2 bg-green-100 rounded-lg">
                                <p className="font-bold text-green-900">{item.received}</p>
                                <p className="text-xs text-green-700">Received</p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Glass Effect Cards */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Glass Effect Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={`glass-${item.id}`}
                          whileHover={{ scale: 1.05, rotateY: 5 }}
                          className="backdrop-blur-md bg-gray-900/80 border border-gray-700 rounded-2xl p-6 shadow-2xl"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-white text-lg mb-1">{item.name}</h3>
                              <p className="text-sm text-gray-300">SKU: {item.sku}</p>
                              <div className="flex items-center mt-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'in-stock' ? 'bg-green-500/20 text-green-300' :
                                  item.status === 'low-stock' ? 'bg-yellow-500/20 text-yellow-300' :
                                  'bg-red-500/20 text-red-300'
                                }`}>
                                  {item.status.replace('-', ' ').toUpperCase()}
                                </span>
                                <span className="ml-2 text-xs text-gray-400">{item.category}</span>
                              </div>
                            </div>
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                              <Package className="w-6 h-6 text-white" />
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-300">Current Stock</span>
                              <span className="font-bold text-white">{item.quantity} {item.unit}</span>
                            </div>
                            
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-300">Stock Level</span>
                                <span className="text-white">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                              </div>
                              <div className="w-full bg-gray-700/50 rounded-full h-2">
                                <motion.div 
                                  className={`h-2 rounded-full ${
                                    item.quantity < item.minStock ? 'bg-red-400' :
                                    item.quantity < item.maxStock * 0.3 ? 'bg-yellow-400' : 'bg-green-400'
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center p-2 bg-blue-500/20 rounded-lg">
                                <p className="font-bold text-blue-300">{item.inTransit}</p>
                                <p className="text-xs text-blue-200">In Transit</p>
                              </div>
                              <div className="text-center p-2 bg-yellow-500/20 rounded-lg">
                                <p className="font-bold text-yellow-300">{item.ordered}</p>
                                <p className="text-xs text-yellow-200">Ordered</p>
                              </div>
                              <div className="text-center p-2 bg-green-500/20 rounded-lg">
                                <p className="font-bold text-green-300">{item.received}</p>
                                <p className="text-xs text-green-200">Received</p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Neumorphic Cards */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Neumorphic Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={`neumorphic-${item.id}`}
                          whileHover={{ scale: 1.02 }}
                          className="bg-gray-100 rounded-2xl p-6 shadow-[inset_2px_2px_8px_rgba(0,0,0,0.1),inset_-2px_-2px_8px_rgba(255,255,255,0.8)]"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-800 text-lg mb-1">{item.name}</h3>
                              <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                              <div className="flex items-center mt-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'in-stock' ? 'bg-green-200 text-green-800' :
                                  item.status === 'low-stock' ? 'bg-yellow-200 text-yellow-800' :
                                  'bg-red-200 text-red-800'
                                }`}>
                                  {item.status.replace('-', ' ').toUpperCase()}
                                </span>
                                <span className="ml-2 text-xs text-gray-500">{item.category}</span>
                              </div>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center shadow-[inset_2px_2px_4px_rgba(0,0,0,0.1)]">
                              <Package className="w-6 h-6 text-gray-600" />
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Current Stock</span>
                              <span className="font-bold text-gray-800">{item.quantity} {item.unit}</span>
                            </div>
                            
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Stock Level</span>
                                <span className="text-gray-800">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 shadow-[inset_1px_1px_2px_rgba(0,0,0,0.1)]">
                                <motion.div 
                                  className={`h-2 rounded-full ${
                                    item.quantity < item.minStock ? 'bg-red-400' :
                                    item.quantity < item.maxStock * 0.3 ? 'bg-yellow-400' : 'bg-green-400'
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center p-2 bg-blue-200 rounded-lg shadow-[inset_1px_1px_2px_rgba(0,0,0,0.1)]">
                                <p className="font-bold text-blue-800">{item.inTransit}</p>
                                <p className="text-xs text-blue-600">In Transit</p>
                              </div>
                              <div className="text-center p-2 bg-yellow-200 rounded-lg shadow-[inset_1px_1px_2px_rgba(0,0,0,0.1)]">
                                <p className="font-bold text-yellow-800">{item.ordered}</p>
                                <p className="text-xs text-yellow-600">Ordered</p>
                              </div>
                              <div className="text-center p-2 bg-green-200 rounded-lg shadow-[inset_1px_1px_2px_rgba(0,0,0,0.1)]">
                                <p className="font-bold text-green-800">{item.received}</p>
                                <p className="text-xs text-green-600">Received</p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Gradient Cards */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Gradient Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={`gradient-${item.id}`}
                          whileHover={{ y: -10, rotateY: 10 }}
                          className={`rounded-2xl p-6 text-white shadow-xl ${
                            index === 0 ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                            index === 1 ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
                            'bg-gradient-to-br from-green-500 to-emerald-500'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-white text-lg mb-1">{item.name}</h3>
                              <p className="text-sm text-white/80">SKU: {item.sku}</p>
                              <div className="flex items-center mt-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'in-stock' ? 'bg-white/20 text-white' :
                                  item.status === 'low-stock' ? 'bg-yellow-500/30 text-yellow-100' :
                                  'bg-red-500/30 text-red-100'
                                }`}>
                                  {item.status.replace('-', ' ').toUpperCase()}
                                </span>
                                <span className="ml-2 text-xs text-white/70">{item.category}</span>
                              </div>
                            </div>
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                              <Package className="w-6 h-6 text-white" />
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-white/80">Current Stock</span>
                              <span className="font-bold text-white">{item.quantity} {item.unit}</span>
                            </div>
                            
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-white/80">Stock Level</span>
                                <span className="text-white">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                              </div>
                              <div className="w-full bg-white/20 rounded-full h-2">
                                <motion.div 
                                  className="h-2 rounded-full bg-white/60"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center p-2 bg-white/20 rounded-lg">
                                <p className="font-bold text-white">{item.inTransit}</p>
                                <p className="text-xs text-white/80">In Transit</p>
                              </div>
                              <div className="text-center p-2 bg-white/20 rounded-lg">
                                <p className="font-bold text-white">{item.ordered}</p>
                                <p className="text-xs text-white/80">Ordered</p>
                              </div>
                              <div className="text-center p-2 bg-white/20 rounded-lg">
                                <p className="font-bold text-white">{item.received}</p>
                                <p className="text-xs text-white/80">Received</p>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Horizontal Layout Cards */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Horizontal Layout Cards</h3>
                    <div className="space-y-4">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={`horizontal-${item.id}`}
                          whileHover={{ x: 10, scale: 1.02 }}
                          className="bg-white rounded-xl border border-gray-200 p-4 shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Package className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="font-bold text-gray-900 text-lg truncate">{item.name}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                                  item.status === 'low-stock' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {item.status.replace('-', ' ').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">SKU: {item.sku} • {item.category}</p>
                              <div className="flex items-center space-x-6">
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-gray-900">{item.quantity}</div>
                                  <div className="text-xs text-gray-500">{item.unit}</div>
                                </div>
                                <div className="flex-1">
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-600">Stock Level</span>
                                    <span className="text-gray-900">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <motion.div 
                                      className={`h-2 rounded-full ${
                                        item.quantity < item.minStock ? 'bg-red-500' :
                                        item.quantity < item.maxStock * 0.3 ? 'bg-yellow-500' : 'bg-green-500'
                                      }`}
                                      initial={{ width: 0 }}
                                      animate={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
                                      transition={{ duration: 1, delay: index * 0.2 }}
                                    />
                                  </div>
                                </div>
                                <div className="flex space-x-2">
                                  <div className="text-center">
                                    <div className="text-sm font-bold text-blue-600">{item.inTransit}</div>
                                    <div className="text-xs text-gray-500">Transit</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-sm font-bold text-yellow-600">{item.ordered}</div>
                                    <div className="text-xs text-gray-500">Ordered</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Compact Info Cards */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Compact Info Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={`compact-${item.id}`}
                          whileHover={{ scale: 1.05, rotateZ: 2 }}
                          className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200 shadow-md"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{item.name}</h3>
                                <p className="text-xs text-gray-500">{item.sku}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">{item.quantity}</div>
                              <div className="text-xs text-gray-500">{item.unit}</div>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Category:</span>
                              <span className="font-medium text-gray-900">{item.category}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Status:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                                item.status === 'low-stock' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {item.status.replace('-', ' ').toUpperCase()}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Stock Level:</span>
                              <span className="font-medium text-gray-900">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>In Transit: {item.inTransit}</span>
                              <span>Ordered: {item.ordered}</span>
                              <span>Received: {item.received}</span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Dashboard Style Cards */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Dashboard Style Cards</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => (
                        <motion.div
                          key={`dashboard-${item.id}`}
                          whileHover={{ y: -5 }}
                          className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                item.status === 'in-stock' ? 'bg-green-100' :
                                item.status === 'low-stock' ? 'bg-yellow-100' : 'bg-red-100'
                              }`}>
                                <Package className={`w-6 h-6 ${
                                  item.status === 'in-stock' ? 'text-green-600' :
                                  item.status === 'low-stock' ? 'text-yellow-600' : 'text-red-600'
                                }`} />
                              </div>
                              <div>
                                <h3 className="font-bold text-gray-900">{item.name}</h3>
                                <p className="text-sm text-gray-500">{item.category}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">{item.quantity}</div>
                              <div className="text-sm text-gray-500">{item.unit}</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Stock Level</span>
                                <span className="text-gray-900">{Math.round((item.quantity / item.maxStock) * 100)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <motion.div 
                                  className={`h-2 rounded-full ${
                                    item.quantity < item.minStock ? 'bg-red-500' :
                                    item.quantity < item.maxStock * 0.3 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((item.quantity / item.maxStock) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-blue-50 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-blue-600">{item.inTransit}</div>
                                <div className="text-xs text-blue-500">In Transit</div>
                              </div>
                              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-yellow-600">{item.ordered}</div>
                                <div className="text-xs text-yellow-500">Ordered</div>
                              </div>
                              <div className="bg-green-50 rounded-lg p-3 text-center">
                                <div className="text-lg font-bold text-green-600">{item.received}</div>
                                <div className="text-xs text-green-500">Received</div>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <span className="text-sm text-gray-500">SKU: {item.sku}</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                                item.status === 'low-stock' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {item.status.replace('-', ' ').toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Inventory Levels Section */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">24. Inventory Levels</h2>
                  
                  {/* Circular Progress Indicators */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Circular Progress Indicators</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                      {inventoryData.slice(0, 6).map((item, index) => {
                        const percentage = Math.round((item.quantity / item.maxStock) * 100);
                        const radius = 40;
                        const circumference = 2 * Math.PI * radius;
                        const strokeDashoffset = circumference - (percentage / 100) * circumference;
                        
                        return (
                          <motion.div
                            key={`circular-${item.id}`}
                            whileHover={{ scale: 1.1 }}
                            className="text-center"
                          >
                            <div className="relative w-24 h-24 mx-auto mb-3">
                              <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                                <circle
                                  cx="50"
                                  cy="50"
                                  r={radius}
                                  stroke="currentColor"
                                  strokeWidth="8"
                                  fill="none"
                                  className="text-gray-200"
                                />
                                <motion.circle
                                  cx="50"
                                  cy="50"
                                  r={radius}
                                  stroke="currentColor"
                                  strokeWidth="8"
                                  fill="none"
                                  strokeLinecap="round"
                                  className={`${
                                    percentage < 30 ? 'text-red-500' :
                                    percentage < 60 ? 'text-yellow-500' : 'text-green-500'
                                  }`}
                                  initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
                                  animate={{ strokeDashoffset }}
                                  transition={{ duration: 1, delay: index * 0.1 }}
                                />
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="text-lg font-bold text-gray-900">{percentage}%</div>
                                  <div className="text-xs text-gray-600">{item.quantity}</div>
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>
                            <div className="text-xs text-gray-600">{item.category}</div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bar Chart Visualizations */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Bar Chart Visualizations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Stock Levels by Category</h4>
                        <div className="space-y-3">
                          {categoryData.map((category, index) => (
                            <motion.div
                              key={category.name}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex items-center space-x-3"
                            >
                              <div className="w-20 text-sm font-medium text-gray-700">{category.name}</div>
                              <div className="flex-1 bg-gray-200 rounded-full h-4">
                                <motion.div
                                  className="h-4 rounded-full"
                                  style={{ backgroundColor: category.color }}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${category.value}%` }}
                                  transition={{ duration: 1, delay: index * 0.1 }}
                                />
                              </div>
                              <div className="w-12 text-sm font-medium text-gray-900">{category.value}%</div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Status Distribution</h4>
                        <div className="space-y-3">
                          {statusData.map((status, index) => (
                            <motion.div
                              key={status.name}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex items-center space-x-3"
                            >
                              <div className="w-20 text-sm font-medium text-gray-700">{status.name}</div>
                              <div className="flex-1 bg-gray-200 rounded-full h-4">
                                <motion.div
                                  className="h-4 rounded-full"
                                  style={{ backgroundColor: status.color }}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${status.value}%` }}
                                  transition={{ duration: 1, delay: index * 0.1 }}
                                />
                              </div>
                              <div className="w-12 text-sm font-medium text-gray-900">{status.value}%</div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gauge Charts */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Gauge Charts</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inventoryData.slice(0, 3).map((item, index) => {
                        const percentage = Math.round((item.quantity / item.maxStock) * 100);
                        const angle = (percentage / 100) * 180;
                        
                        return (
                          <motion.div
                            key={`gauge-${item.id}`}
                            whileHover={{ scale: 1.05 }}
                            className="bg-white rounded-xl border border-gray-200 p-6 text-center"
                          >
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">{item.name}</h4>
                            <div className="relative w-32 h-16 mx-auto mb-4">
                              <svg className="w-32 h-16" viewBox="0 0 100 50">
                                {/* Background arc */}
                                <path
                                  d="M 10 40 A 30 30 0 0 1 90 40"
                                  stroke="currentColor"
                                  strokeWidth="8"
                                  fill="none"
                                  className="text-gray-200"
                                />
                                {/* Progress arc */}
                                <motion.path
                                  d="M 10 40 A 30 30 0 0 1 90 40"
                                  stroke="currentColor"
                                  strokeWidth="8"
                                  fill="none"
                                  strokeLinecap="round"
                                  className={`${
                                    percentage < 30 ? 'text-red-500' :
                                    percentage < 60 ? 'text-yellow-500' : 'text-green-500'
                                  }`}
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: percentage / 100 }}
                                  transition={{ duration: 1, delay: index * 0.2 }}
                                />
                              </svg>
                              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                                <div className="text-2xl font-bold text-gray-900">{percentage}%</div>
                                <div className="text-sm text-gray-600">{item.quantity} / {item.maxStock}</div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">{item.category}</div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Liquid Fill Animations */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Liquid Fill Animations</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                      {inventoryData.slice(0, 6).map((item, index) => {
                        const percentage = Math.round((item.quantity / item.maxStock) * 100);
                        
                        return (
                          <motion.div
                            key={`liquid-${item.id}`}
                            whileHover={{ scale: 1.1 }}
                            className="text-center"
                          >
                            <div className="relative w-20 h-24 mx-auto mb-3 bg-gray-200 rounded-lg overflow-hidden">
                              <motion.div
                                className={`absolute bottom-0 w-full ${
                                  percentage < 30 ? 'bg-red-500' :
                                  percentage < 60 ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                initial={{ height: 0 }}
                                animate={{ height: `${percentage}%` }}
                                transition={{ duration: 1, delay: index * 0.1 }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="text-sm font-bold text-gray-900">{percentage}%</div>
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>
                            <div className="text-xs text-gray-600">{item.quantity}</div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 3D Cube Visualizations */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">3D Cube Visualizations</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                      {inventoryData.slice(0, 6).map((item, index) => {
                        const percentage = Math.round((item.quantity / item.maxStock) * 100);
                        
                        return (
                          <motion.div
                            key={`cube-${item.id}`}
                            whileHover={{ scale: 1.1, rotateY: 15, rotateX: 5 }}
                            className="relative w-20 h-20 mx-auto mb-3"
                          >
                            <motion.div 
                              className={`w-full h-full rounded-lg flex items-center justify-center text-white font-bold shadow-lg ${
                                percentage < 30 ? 'bg-gradient-to-br from-red-500 to-red-600' :
                                percentage < 60 ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 
                                'bg-gradient-to-br from-green-500 to-green-600'
                              }`}
                              whileHover={{ 
                                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                                scale: 1.05
                              }}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                              <div className="text-center">
                                <div className="text-lg font-bold">{percentage}%</div>
                                <div className="text-xs opacity-90">{item.quantity}</div>
                              </div>
                            </motion.div>
                            <div className="text-center mt-2">
                              <div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>
                              <div className="text-xs text-gray-600">{item.category}</div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Animated Number Counters */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Animated Number Counters</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                      {inventoryData.slice(0, 6).map((item, index) => (
                        <motion.div
                          key={`counter-${item.id}`}
                          whileHover={{ scale: 1.05 }}
                          className="bg-white rounded-xl border border-gray-200 p-4 text-center"
                        >
                          <div className="text-2xl font-bold text-gray-900 mb-2">
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                              {item.quantity}
                            </motion.span>
                          </div>
                          <div className="text-sm font-medium text-gray-700 mb-1">{item.name}</div>
                          <div className="text-xs text-gray-600">{item.unit}</div>
                          <div className={`mt-2 px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                            item.status === 'low-stock' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.status.replace('-', ' ').toUpperCase()}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Heat Map Visualization */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Heat Map Visualization</h3>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-semibold text-gray-900">Stock Level Heat Map</h4>
                        <div className="text-sm text-gray-600">
                          Total Items: {inventoryData.length} • 
                          Low Stock: {inventoryData.filter(item => (item.quantity / item.maxStock) * 100 < 30).length} • 
                          Medium Stock: {inventoryData.filter(item => (item.quantity / item.maxStock) * 100 >= 30 && (item.quantity / item.maxStock) * 100 < 60).length} • 
                          High Stock: {inventoryData.filter(item => (item.quantity / item.maxStock) * 100 >= 60).length}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-5 gap-3 mb-6">
                        {inventoryData.map((item, index) => {
                          const percentage = Math.round((item.quantity / item.maxStock) * 100);
                          const intensity = Math.min(percentage / 100, 1);
                          
                          return (
                            <motion.div
                              key={`heat-${item.id}`}
                              whileHover={{ scale: 1.1, zIndex: 10 }}
                              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-white text-xs font-medium cursor-pointer relative group ${
                                percentage < 30 ? 'bg-gradient-to-br from-red-500 to-red-600' :
                                percentage < 60 ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 
                                'bg-gradient-to-br from-green-500 to-green-600'
                              }`}
                              style={{ opacity: 0.4 + (intensity * 0.6) }}
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 0.4 + (intensity * 0.6), scale: 1 }}
                              transition={{ duration: 0.5, delay: index * 0.05 }}
                            >
                              <div className="text-center">
                                <div className="font-bold text-sm">{percentage}%</div>
                                <div className="text-xs opacity-90">{item.quantity}</div>
                              </div>
                              
                              {/* Tooltip on hover */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                                <div className="font-semibold">{item.name}</div>
                                <div>SKU: {item.sku}</div>
                                <div>Category: {item.category}</div>
                                <div>Status: {item.status}</div>
                                <div className="w-full h-1 bg-gray-600 rounded-full mt-1">
                                  <div 
                                    className={`h-1 rounded-full ${
                                      percentage < 30 ? 'bg-red-400' :
                                      percentage < 60 ? 'bg-yellow-400' : 'bg-green-400'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span className="font-semibold text-red-800">Low Stock Items</span>
                          </div>
                          <div className="text-sm text-red-700">
                            {inventoryData.filter(item => (item.quantity / item.maxStock) * 100 < 30).map(item => (
                              <div key={item.id} className="flex justify-between py-1">
                                <span>{item.name}</span>
                                <span className="font-medium">{item.quantity}/{item.maxStock}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                            <span className="font-semibold text-yellow-800">Medium Stock Items</span>
                          </div>
                          <div className="text-sm text-yellow-700">
                            {inventoryData.filter(item => (item.quantity / item.maxStock) * 100 >= 30 && (item.quantity / item.maxStock) * 100 < 60).map(item => (
                              <div key={item.id} className="flex justify-between py-1">
                                <span>{item.name}</span>
                                <span className="font-medium">{item.quantity}/{item.maxStock}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-4 h-4 bg-green-500 rounded"></div>
                            <span className="font-semibold text-green-800">High Stock Items</span>
                          </div>
                          <div className="text-sm text-green-700">
                            {inventoryData.filter(item => (item.quantity / item.maxStock) * 100 >= 60).map(item => (
                              <div key={item.id} className="flex justify-between py-1">
                                <span>{item.name}</span>
                                <span className="font-medium">{item.quantity}/{item.maxStock}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-center mt-6 space-x-6">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-red-500 rounded"></div>
                          <span className="text-sm text-gray-600">Low Stock (&lt;30%)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                          <span className="text-sm text-gray-600">Medium Stock (30-60%)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-green-500 rounded"></div>
                          <span className="text-sm text-gray-600">High Stock (&gt;60%)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
             </div>
           </div>
         )}
      </div>
    </div>
  );
};

export default Inventory; 